// Chaînes françaises (langue par défaut). Même forme que en.ts : une clé manquante ou en trop est une
// erreur de type.

import type { en } from './en.ts';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fr: Shape<typeof en> = {
  common: {
    loading: 'Chargement…',
    cancel: 'Annuler',
    continue: 'Continuer',
    save: 'Enregistrer',
    saved: 'Enregistré.',
    edit: 'Modifier',
    delete: 'Supprimer',
    close: 'Fermer',
    more: 'Afficher plus',
    yes: 'Oui',
    no: 'Non',
    timezone: 'Fuseau horaire',
  },
  errors: {
    unavailable: 'Le service est momentanément indisponible. Réessayez dans quelques minutes.',
    rateLimited: 'Trop de tentatives. Patientez une minute puis réessayez.',
    network: 'La connexion a échoué. Vérifiez votre réseau et réessayez.',
    notFound: 'Cette page n’existe pas.',
    home: 'Retour au tableau de bord',
    crashed: 'Un problème est survenu sur cette page.',
    newVersion: 'Une nouvelle version du tableau de bord est disponible.',
    reload: 'Recharger',
  },
  reauth: {
    title: 'Confirmez votre identité',
    why: 'Pour cette modification, saisissez à nouveau votre mot de passe.',
    wrong: 'Ce n’est pas correct. Réessayez.',
    confirm: 'Confirmer',
    useCode: 'Utiliser plutôt un code d’authentification',
    usePassword: 'Utiliser plutôt mon mot de passe',
  },
  auth: {
    footer: 'Surveillance des températures pour les métiers de bouche — votre registre HACCP tenu pour vous.',
    signInTitle: 'Connexion',
    signIn: 'Se connecter',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    forgot: 'Mot de passe oublié ?',
    noAccount: 'Pas encore de compte ?',
    signUp: 'Créer un compte',
    contactUs: 'Nous contacter',
    closedTitle: 'Les comptes sont créés par notre équipe',
    closedBody: 'Nous mettons en place l’organisation de chaque client avec lui : ses sites, ses équipements et ses capteurs. Parlez-nous de vos besoins, nous revenons vers vous.',
    closedInvited: 'Déjà client ? Votre compte se crée depuis l’invitation que votre organisation ou notre équipe vous a envoyée par e-mail.',
    haveAccountSignIn: 'J’ai déjà un compte',
    useLink: 'Recevoir plutôt un lien de connexion par e-mail',
    usePassword: 'Me connecter avec mon mot de passe',
    sendLink: 'Envoyer le lien',
    checkInbox: 'Consultez votre boîte de réception',
    linkSent: 'Si un compte existe pour {{email}}, nous lui avons envoyé un lien. Il est valable 15 minutes.',
    backToSignIn: 'Retour à la connexion',
    invalidCredentials: 'Adresse e-mail ou mot de passe incorrect.',
    breached: 'Ce mot de passe figure dans une fuite de données connue. Choisissez-en un autre.',
    invalidLink: 'Ce lien est invalide ou a expiré',
    invalidCode: 'Code incorrect. Vérifiez l’heure de votre appareil et réessayez.',
    challengeExpired: 'La connexion a pris trop de temps. Recommencez.',
    twoFactorTitle: 'Vérification en deux étapes',
    totpHint: 'Saisissez le code à 6 chiffres affiché par votre application d’authentification.',
    recoveryHint: 'Saisissez l’un des codes de secours conservés lors de l’activation de la vérification en deux étapes.',
    totpCode: 'Code d’authentification',
    recoveryCode: 'Code de secours',
    useRecovery: 'Utiliser un code de secours',
    useTotp: 'Utiliser mon application d’authentification',
    verify: 'Vérifier',
    signUpTitle: 'Créez votre compte',
    signInToContinue: 'Créez un compte ou connectez-vous pour continuer.',
    name: 'Votre nom',
    passwordHint: 'Au moins 12 caractères. Une courte phrase convient très bien.',
    createAccount: 'Créer mon compte',
    haveAccount: 'Vous avez déjà un compte ?',
    verifySent: 'Nous avons envoyé un lien de confirmation à {{email}}. Ouvrez-le pour activer votre compte.',
    verifying: 'Confirmation de votre adresse…',
    verifyFailed: 'Connectez-vous pour demander un nouvel e-mail de confirmation, ou créez à nouveau votre compte.',
    emailVerified: 'Votre adresse est confirmée',
    signingIn: 'Connexion en cours…',
    openingSupport: 'Ouverture de la session d’assistance…',
    supportLinkFailed: 'Un lien d’assistance ne sert qu’une fois, dans les 5 minutes, et seulement si l’organisation autorise l’accès de l’assistance. Créez-en un nouveau depuis le back-office.',
    supportEnded: 'La session d’assistance est terminée',
    supportEndedBody: 'Vous pouvez fermer cette fenêtre.',
    linkFailed: 'Un lien de connexion ne sert qu’une fois et pendant 15 minutes. Demandez-en un nouveau.',
    forgotTitle: 'Réinitialiser votre mot de passe',
    forgotHint: 'Saisissez votre adresse e-mail : nous vous enverrons un lien pour choisir un nouveau mot de passe.',
    sendResetLink: 'Envoyer le lien',
    resetSent: 'Si un compte existe pour {{email}}, nous lui avons envoyé un lien. Il est valable 1 heure.',
    resetTitle: 'Choisissez un nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    setPassword: 'Enregistrer le mot de passe',
    passwordChanged: 'Votre mot de passe a été modifié',
    passwordChangedHint: 'Vos autres sessions ont été déconnectées. Vous pouvez vous connecter avec le nouveau mot de passe.',
  },
  invite: {
    title: 'Rejoindre une organisation',
    signInFirst: 'Connectez-vous, ou créez un compte avec l’adresse à laquelle l’invitation a été envoyée.',
    joining: 'Vous rejoignez l’organisation…',
    missing: 'Le lien d’invitation est incomplet. Ouvrez-le à nouveau depuis l’e-mail.',
    failed: 'L’invitation n’a pas pu être acceptée',
    failedBody: 'Elle a peut-être expiré ou été révoquée, ou bien elle a été envoyée à une autre adresse que {{email}}. Demandez-en une nouvelle.',
    invalidTitle: 'Cette invitation n’est plus valable',
    invalidBody: 'Elle a expiré, a déjà servi ou a été remplacée. Demandez-en une nouvelle à votre contact : la personne qui vous a invité, ou notre équipe.',
    joinTitle: 'Rejoindre {{org}}',
    forRole: 'Vous êtes invité en tant que {{role}}.',
    forAddress: 'L’invitation a été envoyée à {{email}} : votre compte utilise cette adresse.',
    language: 'Langue',
    createAndJoin: 'Créer mon compte et rejoindre',
    accountExists: 'Vous avez déjà un compte',
    accountExistsBody: 'Un compte existe pour {{email}}. Connectez-vous avec : l’invitation sera acceptée juste après.',
  },
  shell: {
    organisation: 'Organisation',
    navigation: 'Navigation principale',
    profile: 'Mon profil',
    newOrg: 'Nouvelle organisation',
    signOut: 'Se déconnecter',
    themeDark: 'Passer au thème sombre',
    themeLight: 'Passer au thème clair',
    supportReadOnly: 'Session d’assistance en tant que {{name}}, en lecture seule, jusqu’à {{time}}. Chaque accès est enregistré.',
    supportWrite: 'Session d’assistance en tant que {{name}}, modifications permises, jusqu’à {{time}}. Chaque action est enregistrée.',
    endSupport: 'Terminer la session',
    verifyEmail: 'Confirmez votre adresse : nous avons envoyé un lien à {{email}}.',
    twoFactorRequired: 'Vérification en deux étapes obligatoire',
    twoFactorRequiredBody: '{{org}} exige la vérification en deux étapes pour votre rôle. Activez-la pour continuer.',
    enableTwoFactor: 'L’activer',
    restricted: 'Compte en lecture seule',
    restrictedOwner: 'L’abonnement n’est pas réglé : les modifications sont bloquées et les alertes ne partent plus que par e-mail.',
    restrictedMember: 'L’abonnement n’est pas réglé : les modifications sont bloquées et les alertes ne partent plus que par e-mail. Contactez le titulaire du compte.',
    pastDue: 'Le dernier paiement a échoué. Mettez à jour le moyen de paiement pour garder tous vos accès.',
    pastDueRevolut: 'Le dernier renouvellement a échoué. Réglez-le pour garder tous vos accès.',
    trialEnds: 'Votre essai gratuit se termine le {{date}}.',
    canceled: 'Abonnement terminé',
    canceledOwner: 'Les modifications sont bloquées et la surveillance s’arrête le {{date}}. Réabonnez-vous pour garder vos données.',
    canceledMember: 'Les modifications sont bloquées et la surveillance va s’arrêter. Contactez le titulaire du compte.',
    billingAction: 'Abonnement',
    erasurePending: 'Cette organisation et toutes ses données seront effacées le {{date}}.',
    erasureCancel: 'Annuler l’effacement',
  },
  nav: {
    overview: 'Vue d’ensemble',
    alarms: 'Alarmes',
    devices: 'Boîtiers',
    settings: 'Paramètres',
    maintenance: 'Maintenance',
    energy: 'Énergie',
    haccp: 'HACCP',
    lora: 'LoRa',
    exports: 'Exports',
  },
  tour: {
    start: 'Visite guidée',
    setup: 'Assistant de configuration',
    label: 'Visite guidée de l’interface',
    step: 'Étape {{n}} / {{count}}',
    previous: 'Précédent',
    next: 'Suivant',
    finish: 'Terminer',
    menuAdmin: 'Votre profil et la déconnexion. Relancez ici la visite guidée quand vous voulez, ainsi que l’assistant de configuration pour ajouter sites, équipements et boîtiers.',
    steps: {
      welcome: { title: 'Bienvenue dans Maillon Froid', body: 'En une minute, voici où trouver l’essentiel. Flèches ou Entrée pour avancer, Échap pour quitter.' },
      org: { title: 'Votre organisation', body: 'L’organisation affichée. Si vous en suivez plusieurs, changez-en ici.' },
      navOverview: { title: 'Vue d’ensemble', body: 'Tous vos équipements par site, avec leur température en direct. C’est la page d’accueil.' },
      navAlarms: { title: 'Alarmes', body: 'Les alarmes en cours et leur historique. Le chiffre rouge compte celles qui sont ouvertes ; acquittez-les ici, avec l’action corrective.' },
      navDevices: { title: 'Boîtiers', body: 'Les boîtiers et leurs sondes : état, batterie, signal. « Ajouter un boîtier » en installe un nouveau.' },
      navLora: { title: 'LoRa', body: 'Vos bases LoRa, qui relaient les capteurs radio.' },
      navHaccp: { title: 'HACCP', body: 'Les relevés du plan HACCP (refroidissements, réceptions, nettoyages), à saisir aussi sur téléphone.' },
      navMaintenance: { title: 'Maintenance', body: 'Les interventions sur vos équipements, leurs coûts et l’entretien à prévoir.' },
      navEnergy: { title: 'Énergie', body: 'La consommation électrique de vos équipements et ce qu’elle coûte.' },
      navSettings: { title: 'Paramètres', body: 'Sites, équipements, seuils d’alarme, équipe et notifications. Les exports PDF et CSV pour un contrôle sont dans Paramètres → Exports.' },
      navExports: { title: 'Exports', body: 'Les rapports PDF et CSV de vos températures et relevés, pour un contrôle sanitaire.' },
      live: { title: 'En direct', body: 'Vert : les valeurs arrivent en direct. Orange : reconnexion. Gris : hors ligne, la page n’est plus à jour.' },
      alarmBanner: { title: 'Alarmes en cours', body: 'Ce bandeau apparaît dès qu’une alarme est ouverte. « Voir les alarmes » ouvre la liste.' },
      views: { title: 'Trois affichages', body: 'Tuiles, courbes de tous les équipements, ou carte des sites et des camions.' },
      asset: { title: 'Un équipement', body: 'La bordure et le badge donnent l’état : vert OK, orange avertissement, rouge alarme, violet hors ligne, gris sans données. Touchez la tuile pour ouvrir sa courbe et son historique.' },
      menu: { title: 'Votre menu', body: 'Votre profil et la déconnexion. Relancez ici la visite guidée quand vous voulez.' },
      done: { title: 'C’est parti', body: 'Vous savez où tout se trouve. La visite guidée reste dans votre menu, en haut à droite.' },
    },
  },
  live: {
    connecting: 'Connexion…',
    live: 'En direct',
    offline: 'Hors direct — actualisé chaque minute',
  },
  onboarding: {
    title: 'Créez votre organisation',
    subtitle: 'Votre entreprise ou votre établissement : ses sites, ses équipements et son équipe sont gérés ensemble.',
    name: 'Nom',
    nameHint: 'Tel que votre équipe le connaît, par ex. « Boulangerie Martin ».',
    plan: 'Formule',
    trial: 'Gratuit pendant {{days}} jours, sans carte bancaire. Vous pouvez changer de formule à tout moment.',
    planLimits: 'jusqu’à {{devices}} boîtiers et {{users}} utilisateurs',
    create: 'Créer l’organisation',
    verifyFirst: 'Confirmez d’abord votre adresse e-mail : ouvrez le lien que nous vous avons envoyé.',
    closedTitle: 'Votre organisation est en préparation',
    closedBody: 'Notre équipe met en place l’organisation de chaque client, puis invite ses membres. Vous n’avez pas à en créer une.',
    closedInvitation: 'Dès que vous êtes invité, ouvrez le lien de l’e-mail envoyé à {{email}} : vous arriverez dans votre organisation.',
    closedContact: 'Une question ? Contactez-nous',
  },
  overview: {
    title: 'Vue d’ensemble',
    live: 'Mise à jour en direct',
    fromDatabase: 'Dernières valeurs connues',
    activeAlarms_one: '{{count}} alarme en cours',
    activeAlarms_other: '{{count}} alarmes en cours',
    awaiting_one: '{{count}} alarme à acquitter',
    awaiting_other: '{{count}} alarmes à acquitter',
    alarmsHint: 'Acquittez chaque alarme et notez ce que vous avez fait : cela figure au registre HACCP.',
    seeAlarms: 'Voir les alarmes',
    noSites: 'Aucun site pour l’instant',
    noSitesHint: 'Commencez par un site (une boutique, une cuisine, un entrepôt), puis ajoutez-y les frigos et congélateurs.',
    addSite: 'Ajouter un site',
    noAssets: 'Aucun équipement pour l’instant',
    noAssetsHint: 'Ajoutez un frigo, un congélateur ou une chambre froide, puis associez-lui un boîtier.',
    addAsset: 'Ajouter un équipement',
    siteEmpty: 'Aucun équipement sur ce site.',
    allSites: 'Tous les sites',
    site: 'Site',
    doorOpen: 'Porte ouverte depuis {{duration}}',
    toAcknowledge_one: '{{count}} à acquitter',
    toAcknowledge_other: '{{count}} à acquitter',
    seen: 'Vu {{ago}}',
    neverSeen: 'Aucune mesure pour l’instant',
    position: 'Position {{ago}}',
    view: 'Affichage',
    viewCards: 'Tuiles',
    viewCharts: 'Courbes',
    viewMap: 'Carte',
  },
  map: {
    region: 'Carte des sites et des véhicules',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    attribution: 'Crédits de la carte',
    unavailable: 'La carte ne peut pas s’afficher ici. La liste reprend tous les sites et véhicules.',
    loading: 'Chargement de la carte…',
    site: 'Site',
    vehicle: 'Véhicule',
    legend: 'Légende',
    statusFilter: 'État',
    allStatuses: 'Tous les états',
    problems: 'Problèmes seulement',
    list: 'Sur la carte',
    nothing: 'Rien sur la carte avec ce filtre.',
    noPoints: 'Aucun site sur la carte pour l’instant',
    noPointsHint: 'Situez vos sites dans Paramètres → Sites et équipements : cliquez sur une petite carte ou saisissez les coordonnées.',
    missing: 'Sites sans emplacement',
    missingHint: 'Ils ne sont pas sur la carte. Un propriétaire ou un administrateur peut les situer dans Paramètres → Sites et équipements.',
    place: 'Situer les sites',
    choose: 'Choisissez un site ou un véhicule, sur la carte ou dans la liste, pour voir ses équipements.',
    close: 'Fermer',
    equipment: 'Équipements',
    noEquipment: 'Aucun équipement ici.',
    alarms: 'Alarmes en cours',
    vehicleOf: 'Véhicule du site {{site}}',
    since: 'depuis {{ago}}',
  },
  status: {
    alarm: 'Alarme',
    warning: 'Avertissement',
    offline: 'Hors ligne',
    ok: 'OK',
    no_data: 'Pas de données',
  },
  assetType: {
    fridge: 'Réfrigérateur',
    freezer: 'Congélateur',
    cold_room: 'Chambre froide',
    blast_freezer: 'Cellule de refroidissement',
    truck: 'Véhicule frigorifique',
    other: 'Autre…',
  },
  customType: {
    label: 'Nom du type',
    labelHint: 'Ex. « Cave à vin », « Vitrine », « Extérieur ».',
    min: 'Minimum °C (facultatif)',
    max: 'Maximum °C (facultatif)',
  },
  severity: {
    warning: 'Avertissement',
    critical: 'Critique',
  },
  alarmState: {
    active: 'En cours',
    awaitingAck: 'Terminée, à acquitter',
    acknowledged: 'Acquittée',
    resolved: 'Résolue',
  },
  role: {
    owner: 'Titulaire',
    admin: 'Administrateur',
    operator: 'Opérateur',
    viewer: 'Lecteur',
  },
  roleHint: {
    admin: 'Gère les sites, les équipements, les boîtiers, les règles d’alerte et l’équipe.',
    operator: 'Acquitte les alarmes, note les actions correctives et les remarques, met les alertes en pause quelques heures.',
    viewer: 'Consulte les températures, les alarmes et le registre, et télécharge les exports.',
  },
  alarmType: {
    high: 'Température trop haute',
    low: 'Température trop basse',
    rate_of_change: 'Variation brutale de température',
    offline: 'Boîtier hors ligne',
    door_open: 'Porte restée ouverte',
    low_battery: 'Batterie faible',
    probe_fault: 'Sonde défaillante',
    power_loss: 'Coupure de courant',
    no_consumption: 'Aucune consommation',
    compressor_continuous: 'Compresseur en marche continue',
    short_cycling: 'Cycles courts du compresseur',
    meter_offline: 'Compteur hors ligne',
    cooling_late: 'Refroidissement trop lent',
  },
  alarms: {
    title: 'Alarmes',
    show: 'Afficher',
    open: 'En cours ou à acquitter',
    closed: 'Clôturées',
    severity: 'Gravité',
    allSeverities: 'Toutes les gravités',
    none: 'Aucune alarme sur cette période.',
    noneOpen: 'Aucune alarme en cours. Tout va bien.',
    alarm: 'Alarme',
    asset: 'Équipement',
    since: 'Depuis',
    value: 'Valeur',
    state: 'État',
    retrospective: 'enregistrée hors ligne',
    deviceLevel: 'Boîtier',
    lasted: 'a duré {{duration}}',
    notFound: 'Cette alarme n’existe pas ou n’appartient pas à cette organisation.',
    notFoundBody: 'Elle appartient peut-être à une organisation dont vous n’êtes plus membre.',
    retrospectiveHint: 'Le boîtier était hors ligne au moment des faits : l’alarme a été levée à l’arrivée de ses mesures.',
    summary: 'Résumé',
    conditionSince: 'Condition depuis',
    triggeredAt: 'Alarme levée',
    clearedAt: 'Retour à la normale',
    triggerValue: 'Valeur au déclenchement',
    peak: 'Pire valeur',
    level: 'Niveau d’escalade',
    acknowledgedBy: 'Acquittée par',
    rule: 'Règle',
    actions: 'Actions correctives',
    noActions: 'Aucune action corrective notée pour l’instant.',
    disposition: 'Produits',
    dispositionValue: 'Produits : {{value}}',
    photo: 'Photo',
    timeline: 'Chronologie',
    notifications: 'Notifications envoyées',
    noNotifications: 'Aucune notification envoyée.',
    recipient: 'Destinataire',
    channel: 'Canal',
    sent: 'Envoyée',
    delivery: 'Remise',
    levelN: 'niveau {{n}}',
    alreadyAcknowledged: 'Quelqu’un vient d’acquitter cette alarme. La page a été actualisée.',
    category: 'Ce qui a été fait',
    dispositionHint: 'Ce que sont devenus les produits, par ex. « conservés, moins de 2 h au-dessus de 8 °C » ou « 3 kg jetés ».',
    comment: 'Commentaire',
    photoOptional: 'Photo (facultatif)',
    photoHint: 'JPEG, PNG ou WebP, 10 Mo au plus.',
    acknowledge: 'Acquitter',
    acknowledgeHint: 'Indiquez ce que vous avez fait : l’alarme et l’action figurent au registre HACCP.',
    addAction: 'Ajouter une action corrective',
  },
  alarmEvent: {
    triggered: 'Alarme levée',
    notified: 'Notifications envoyées',
    escalated: 'Transmise au niveau suivant',
    reminder: 'Rappel envoyé',
    acknowledged: 'Acquittée',
    comment: 'Commentaire',
    cleared: 'Retour à la normale',
    resolved: 'Résolue',
    reopened: 'Rouverte',
    muted_excursion: 'Dépassement pendant une pause',
    maintenance: 'Intervention de maintenance enregistrée',
  },
  notificationReason: {
    trigger: 'Alarme',
    escalation: 'Escalade',
    reminder: 'Rappel',
    retrospective: 'Enregistrée hors ligne',
    cleared: 'Retour à la normale',
    fallback: 'Secours',
    advisory: 'Conseil maintenance',
  },
  notificationStatus: {
    queued: 'En attente',
    accepted: 'Envoyée',
    delivered: 'Remise',
    read: 'Lue',
    failed: 'Échec',
    quota_exceeded: 'Quota atteint',
    suppressed_quiet_hours: 'Heures calmes',
  },
  channel: {
    email: 'E-mail',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  correctiveCategory: {
    door_closed: 'Porte refermée',
    moved_products: 'Produits déplacés',
    product_discarded: 'Produits jetés',
    technician_called: 'Technicien appelé',
    probe_displaced: 'Sonde remise en place',
    false_alarm: 'Fausse alarme',
    other: 'Autre',
  },
  assets: {
    notFound: 'Cet équipement n’existe pas ou a été archivé.',
    rules: 'Règles d’alerte',
    csv: 'Télécharger en CSV',
    openAlarms: 'Alarmes en cours',
    temperatures: 'Températures',
    range: 'Période',
    noProbes: 'Aucune sonde sur cet équipement',
    noProbesHint: 'Affectez-lui une sonde d’un boîtier depuis la page Boîtiers.',
    noData: 'Aucune mesure sur cette période.',
    rollupHint: 'Moyennes par {{tier}}, minimum et maximum en grisé.',
    doorSummary_one: 'Porte ouverte une fois, {{total}} au total.',
    doorSummary_other: 'Porte ouverte {{count}} fois, {{total}} au total.',
    statistics: 'Statistiques de la période',
    probe: 'Sonde',
    probes: 'Sondes',
    value: 'Dernière valeur',
    calibration: 'Étalonnage',
    device: 'Boîtier',
    door: 'porte',
    open: 'Ouvrir',
    thresholdHigh: 'Seuil haut {{value}}',
    thresholdLow: 'Seuil bas {{value}}',
  },
  ranges: {
    '24h': '24 h',
    '7d': '7 jours',
    '30d': '30 jours',
    '90d': '90 jours',
    '1y': '1 an',
  },
  tiers: {
    auto: 'automatique',
    raw: 'mesure',
    '5m': '5 minutes',
    '1h': 'heure',
    local_day: 'jour',
  },
  stats: {
    min: 'Min.',
    max: 'Max.',
    avg: 'Moyenne',
    mkt: 'TCM',
    completeness: 'Complétude',
  },
  annotations: {
    title: 'Remarques',
    add: 'Ajouter une remarque',
    hint: 'Expliquez une mesure pour le registre : un dégivrage, une livraison, un nettoyage, une valeur erronée.',
    category: 'Nature',
    from: 'Du',
    to: 'Au',
    body: 'Remarque',
    none: 'Aucune remarque sur cette période.',
    superseded: 'remplacée',
  },
  annotationCategory: {
    correction: 'Correction',
    defrost: 'Dégivrage',
    maintenance: 'Maintenance',
    cleaning: 'Nettoyage',
    delivery: 'Livraison',
    note: 'Remarque',
  },
  devices: {
    title: 'Boîtiers',
    claim: 'Ajouter un boîtier',
    none: 'Aucun boîtier pour l’instant',
    noneHint: 'Chaque boîtier accueille jusqu’à quatre sondes. Ajoutez-le avec le code imprimé sur son étiquette.',
    device: 'Boîtier',
    site: 'Site',
    state: 'État',
    lastSeen: 'Dernier contact',
    battery: 'Batterie',
    signal: 'Signal',
    firmware: 'Micrologiciel',
    mains: 'Secteur',
    online: 'En ligne',
    offline: 'Hors ligne',
    pending: 'En attente de sa première connexion',
    revoked: 'Révoqué',
    released: 'Libéré',
    notFound: 'Ce boîtier n’existe pas ou n’appartient plus à cette organisation.',
    release: 'Libérer',
    revoke: 'Révoquer',
    waiting: 'En attente du boîtier',
    waitingHint: 'Branchez-le à portée d’un réseau Wi-Fi ou mobile : il se connecte tout seul en quelques minutes.',
    health: 'État de santé',
    power: 'Alimentation',
    onBattery: 'Sur batterie',
    lastReset: 'Dernier redémarrage',
    claimedAt: 'Ajouté le',
    sampling: 'Mesures',
    samplingValue: 'toutes les {{sample}} s, envoyées toutes les {{upload}} s',
    configVersion: 'Version de la configuration',
    probes: 'Sondes',
    noProbes: 'Le boîtier n’a pas encore signalé ses sondes.',
    settings: 'Réglages du boîtier',
    label: 'Nom',
    sampleEvery: 'Mesurer toutes les',
    uploadEvery: 'Envoyer toutes les',
    seconds: 'secondes',
    settingsHint: 'Le boîtier applique les nouveaux réglages à sa prochaine connexion.',
    revokeTitle: 'Révoquer ce boîtier ?',
    releaseTitle: 'Libérer ce boîtier ?',
    revokeHint: 'Ses identifiants cessent aussitôt et définitivement de fonctionner : à utiliser en cas de perte ou de vol. Ses mesures sont conservées.',
    releaseHint: 'Il quitte cette organisation et peut être ajouté à une autre. Ses mesures restent ici.',
    probeLimit: 'La limite de sondes de votre formule est atteinte. Changez de formule pour en ajouter.',
    probeLabel: 'Nom de cette sonde',
    probe: 'Sonde',
    lastValue: 'Dernière valeur',
    asset: 'Équipement',
    unassigned: 'Non affectée',
    rename: 'Renommer',
    calibrate: 'Étalonner',
    calibrationTitle: 'Étalonnage — {{probe}}',
    calibrationHint: 'Comparez la sonde à un thermomètre de référence et saisissez la correction à appliquer à ses mesures.',
    offset: 'Correction',
    certificateValidUntil: 'Certificat valable jusqu’au',
    certificate: 'Certificat',
    certificateHint: 'PDF ou image du certificat d’étalonnage, 10 Mo au plus.',
    note: 'Remarque',
    calibrationHistory: 'Historique',
    feeds: 'Flux',
    feedsTitle: 'Flux en direct — {{probe}}',
    feedsHint: 'Un lien secret vers les mesures de cette sonde, pour les outils qui ne peuvent pas se connecter : Home Assistant, Grafana, un tableur. Toute personne qui a le lien peut lire les températures de la sonde (rien d’autre, et sans rien modifier) jusqu’à ce que vous le révoquiez.',
    feedLabel: 'Usage',
    feedLabelHint: 'ex. Home Assistant',
    feedCreate: 'Créer un lien',
    feedOnce: 'Copiez ce lien maintenant',
    feedOnceHint: 'Il n’est affiché qu’une fois. S’il est perdu, révoquez-le et créez-en un autre.',
    feedFormats: 'Ajoutez ?format=csv pour du CSV ou ?format=prometheus pour Prometheus, et &hours=48 pour remonter jusqu’à 168 heures (24 par défaut). 30 requêtes par minute au plus.',
    feedActive: 'Liens actifs',
    feedNone: 'Aucun lien pour l’instant.',
    feedCreated: 'Créé {{when}}',
    feedUsed: 'lu {{when}}',
    feedNeverUsed: 'jamais lu',
    feedCopy: 'Copier',
    feedCopied: 'Copié',
    feedRevoke: 'Révoquer',
    feedRevokeConfirm: 'Révoquer ce lien ? Les outils qui l’utilisent cessent aussitôt de recevoir les mesures de la sonde.',
    feedLimit: 'Cette sonde a déjà 10 liens : révoquez-en un d’abord.',
    from: 'Depuis',
    view: 'Voir',
    live: {
      on: 'En direct',
      starting: 'Mode direct',
      every: 'toutes les {{s}} s',
      left: 'encore {{time}}',
      waiting: 'En attente du boîtier…',
      waitingUntil: 'En attente du boîtier… jusqu’à {{s}} s',
      paused: 'Direct en pause',
      resume: 'Reprendre',
      noValue: 'Pas encore de valeur',
      curve: '10 dernières minutes : de {{min}} à {{max}}',
    },
  },
  probeKind: {
    temperature: 'Température',
    door: 'Porte',
    energy: 'Compteur d’énergie',
  },
  setup: {
    title: 'Configurer {{org}}',
    subtitle: 'Six étapes, environ 15 minutes. Chacune peut être terminée plus tard depuis les Paramètres.',
    later: 'Terminer plus tard',
    steps: 'Étapes de la configuration',
    step: { sites: 'Sites', equipment: 'Équipements', devices: 'Boîtiers', probes: 'Sondes', alarms: 'Alertes', team: 'Équipe' },
    progress: '{{done}} étapes sur {{total}} terminées',
    next: 'Suivant : {{step}}',
    finish: 'Terminer',
    needSite: 'Ajoutez d’abord un site (étape 1).',
    needEquipment: 'Ajoutez d’abord vos équipements (étape 2).',
    resumeTitle: 'Votre configuration n’est pas terminée',
    resume: 'Reprendre la configuration',
    resumeHint: 'Aucune sonde ne mesure encore d’équipement : l’assistant de configuration vous guide pour les boîtiers et leurs sondes.',
    sites: {
      intro: 'Un site est un lieu avec sa propre adresse et son fuseau horaire : un restaurant, une cuisine, un entrepôt. Les relevés journaliers et les rapports suivent son fuseau.',
      name: 'Nom du site', nameHint: 'Ex. « Restaurant, rue du Marché ».', address: 'Adresse (facultatif)', timezone: 'Fuseau horaire',
      add: 'Ajouter le site', addAnother: 'Ajouter un autre site',
    },
    equipment: {
      intro: 'Chaque réfrigérateur, congélateur ou chambre froide à surveiller. Son type fixe les alertes par défaut, que vous pouvez modifier à l’étape Alertes.',
      name: 'Nom', nameHint: 'Comme votre équipe l’appelle, ex. « Frigo 1 ».', type: 'Type', volume: 'Volume en litres (facultatif)',
      template: 'Alertes par défaut :', add: 'Ajouter l’équipement', addAnother: 'Ajouter un autre',
    },
    devices: {
      intro: 'Les boîtiers mesurent et envoient les températures. Chacun porte sur son étiquette un identifiant (CCK-…) et un code.',
      kind: { wifi: 'Boîtier Wi-Fi', loraBase: 'Base LoRa', loraNode: 'Capteur LoRa' },
      wifi: {
        s1: 'Branchez ses sondes, puis alimentez-le avec un adaptateur USB-C.',
        s2: 'Avec votre téléphone, rejoignez le réseau Wi-Fi qui porte son identifiant (mot de passe : les 8 premiers caractères de son code, sans le tiret), ouvrez http://192.168.4.1/ et saisissez votre Wi-Fi.',
        s3: 'Saisissez son identifiant et son code ci-dessous.',
        labelHint: 'Où il se trouve, ex. « Cuisine ».',
      },
      loraBase: {
        s1: 'Placez-la à portée de votre Wi-Fi, le plus haut possible, antenne verticale, et alimentez-la.',
        s2: 'Connectez-la au Wi-Fi comme un boîtier Wi-Fi : son réseau porte son identifiant, le mot de passe est formé des 8 premiers caractères de son code.',
        s3: 'Saisissez son identifiant et son code ci-dessous. Elle relaie ensuite les capteurs LoRa qu’elle capte : voir la page LoRa.',
        labelHint: 'Ex. « Base, cuisine ».',
      },
      loraNode: {
        s1: 'Pour un frigo ou une pièce hors de portée du Wi-Fi : il envoie ses mesures par radio à une base LoRa.',
        s2: 'Réclamez d’abord une base LoRa, puis alimentez le capteur à portée (quelques centaines de mètres, à travers les murs).',
        s3: 'Saisissez son identifiant et son code ci-dessous : il se connecte via la base en une ou deux minutes.',
        labelHint: 'Où il se trouve, ex. « Cave ».',
      },
      claim: 'Réclamer le boîtier',
      waiting: 'En attente du premier contact des boîtiers… (actualisé toutes les 10 s)',
    },
    probes: {
      intro: 'Chaque sonde et chaque compteur électrique de vos boîtiers : indiquez quel équipement il mesure. Les nouvelles sondes apparaissent ici quelques minutes après le premier contact de leur boîtier.',
      none: 'Aucune sonde pour l’instant : elles apparaissent dès qu’un boîtier a envoyé ses premières mesures.',
      labelPlaceholder: 'Nom (facultatif), ex. « Étagère du haut »',
      warmHint: 'Tenez une sonde dans la main : sa valeur monte, vous savez laquelle est laquelle.',
    },
    alarms: {
      intro: 'Les alertes appliquées à chaque équipement. Celles par défaut conviennent le plus souvent ; ajustez-les à votre plan HACCP si besoin.',
      adjust: 'Ajuster', none: 'Aucune alerte sur cet équipement : ajoutez-en au moins une.',
    },
    team: {
      intro: 'Qui reçoit les alertes : invitez votre équipe. Les opérateurs acquittent les alarmes ; les administrateurs gèrent aussi la configuration.',
      invited: 'Invité', email: 'Adresse e-mail', role: 'Rôle', invite: 'Inviter',
      notifications: 'Les alertes partent par e-mail ; ajoutez un numéro SMS, WhatsApp ou Telegram pour les critiques :',
      notificationsLink: 'Mes notifications',
    },
  },
  lora: {
    title: 'Bases LoRa',
    subtitle: 'Les bases relaient par Wi-Fi les mesures de vos capteurs LoRa : ceux hors de portée du Wi-Fi, en cave ou en chambre froide.',
    add: 'Ajouter une base LoRa',
    none: 'Aucune base LoRa pour l’instant',
    noneHint: 'Une base LoRa se place à portée du Wi-Fi et capte par radio les capteurs LoRa installés jusqu’à quelques centaines de mètres, à travers les murs.',
    nodes: 'Capteurs liés',
    working: 'En service',
    weak: 'Signal faible',
    relayed: 'Autres capteurs relayés',
    relayedHint: 'Capteurs LoRa d’autres organisations que votre base relaie : leurs mesures restent chiffrées et invisibles pour vous.',
    lastSeen: 'Dernier contact {{when}}',
    neverSeen: 'Jamais connectée',
    otherOrgs_one: 'relaie aussi {{count}} capteur d’une autre organisation',
    otherOrgs_other: 'relaie aussi {{count}} capteurs d’autres organisations',
    node: 'Capteur',
    signalCol: 'Signal',
    heard: 'Dernière réception',
    inService: 'En service',
    signal: { good: 'Bon', fair: 'Moyen', weak: 'Faible' },
    signalLegend: 'Signal en SF9 : faible sous −7 dB de SNR ou −118 dBm (des trames peuvent se perdre), moyen sous 0 dB ou −105 dBm. La base met à jour le signal de chaque capteur au moins toutes les 10 minutes tant qu’elle l’entend.',
    weakTitle: 'Certains capteurs sont mal captés',
    weakHint: 'Leurs mesures peuvent arriver en retard ou être renvoyées. Placez la base plus haut, antenne verticale et loin du métal, ou rapprochez-la de ces capteurs.',
    nodesTitle: 'Capteurs LoRa captés par cette base',
    noNodes: 'Cette base n’a encore capté aucun de vos capteurs LoRa. Allumez-en un à portée : une fois réclamé, il apparaît ici en quelques minutes.',
    notFound: 'Cet appareil n’est pas une base LoRa de cette organisation.',
    deviceSettings: 'Réglages de l’appareil',
    onboarding: {
      title: 'Ajouter une base LoRa',
      subtitle: 'Environ 5 minutes. Gardez sous la main la base, son alimentation USB-C et son étiquette (identifiant et code).',
      step1: '1. Placer la base',
      place1: 'À portée de votre Wi-Fi (2,4 GHz), branchée sur une alimentation USB-C 5 V qui reste allumée.',
      place2: 'Le plus haut possible, antenne verticale, loin du métal (étagères, parois de chambre froide).',
      place3: 'Entre le Wi-Fi et les capteurs LoRa : quelques centaines de mètres à travers quelques murs passent en général.',
      place4: 'Une base peut servir jusqu’à 16 capteurs LoRa.',
      step2: '2. La connecter au Wi-Fi',
      wifi1: 'Avec votre téléphone, rejoignez le réseau Wi-Fi qui porte l’identifiant de la base (ex. CCK-7Q2M9X4T). Mot de passe : les 8 premiers caractères du code de l’étiquette, sans le tiret.',
      wifi2: 'Ouvrez http://192.168.4.1/, choisissez votre réseau Wi-Fi et saisissez son mot de passe.',
      wifi3: 'La base se connecte : son voyant clignote toutes les 2 secondes jusqu’à ce qu’elle soit réclamée, puis bat comme un cœur.',
      wifiHint: 'Le réseau de configuration se ferme au bout de 15 minutes ; un appui court sur le bouton BOOT de la base le rouvre.',
      step3: '3. La réclamer',
      labelHint: 'Où elle se trouve, ex. « Cuisine, au-dessus de la porte ».',
      claim: 'Réclamer la base',
      claimed: 'Base réclamée',
      step4: '4. La base se connecte',
      waiting: 'En attente du premier contact de la base (quelques minutes une fois sur le Wi-Fi).',
      online: 'La base est en ligne.',
      step5: '5. Ses capteurs LoRa',
      nodesHint: 'Allumez vos capteurs LoRa et réclamez chacun avec son propre code (Boîtiers → Ajouter un boîtier). Ils apparaissent ici, avec leur signal, à mesure que la base les capte.',
      listening: 'À l’écoute… aucun capteur capté pour l’instant.',
      addNode: 'Réclamer un capteur LoRa',
    },
  },
  claim: {
    title: 'Ajouter un boîtier',
    subtitle: 'Le code figure sur l’étiquette sous le boîtier et sur son emballage.',
    hardwareId: 'Identifiant du boîtier',
    hardwareIdHint: 'Par ex. CCK-7Q2M9X4T.',
    code: 'Code d’activation',
    codeHint: 'Douze caractères, sous l’identifiant (tirets facultatifs).',
    labelHint: 'Son emplacement, par ex. « Cuisine — frigo 2 ».',
    submit: 'Ajouter le boîtier',
    wrongCode: 'Cet identifiant et ce code ne correspondent pas. Vérifiez l’étiquette.',
    elsewhere: 'Ce boîtier appartient à une autre organisation. Il doit d’abord y être libéré.',
    limit: 'La limite de boîtiers de votre formule est atteinte. Changez de formule pour en ajouter.',
    claimed: 'Boîtier ajouté',
    step2: 'Mettez-le en marche',
    online: 'Le boîtier est connecté.',
    waiting: 'En attente de la première connexion…',
    power: 'Branchez le boîtier ou insérez ses piles.',
    network: 'Laissez-le à portée du Wi-Fi ou du réseau mobile.',
    wait: 'Il se connecte en quelques minutes ; cette page se met à jour toute seule.',
    step3: 'Affectez ses sondes',
    noProbesYet: 'Les sondes apparaissent ici après la première connexion.',
    done: 'Terminé',
  },
  kiosk: {
    label: 'Kiosque',
    leave: 'Quitter',
    leaveConfirm: 'Quitter le mode kiosque sur cet écran ? Il faudra de nouveau le lien du kiosque, ou une connexion.',
    readOnly: 'Tableau de bord en lecture seule : données et téléchargements. Les modifications se font sur app.maillonfroid.com.',
    opening: 'Ouverture du kiosque…',
    invalid: 'Ce lien de kiosque ne fonctionne plus',
    invalidBody: 'Il a été révoqué, ou il est incomplet. Demandez un nouveau lien à un administrateur de votre organisation (Paramètres → Kiosques).',
  },
  kiosks: {
    subtitle: 'Un lien de kiosque affiche le tableau de bord sur un écran mural ou une tablette sans connexion : données et téléchargements uniquement, pour toute l’organisation ou un site, jusqu’à ce que vous le révoquiez.',
    new: 'Nouveau lien de kiosque',
    name: 'Nom',
    nameHint: 'L’emplacement de l’écran, par ex. Cuisine',
    scope: 'Affiche',
    wholeOrg: 'Toute l’organisation',
    create: 'Créer le lien',
    once: 'Copiez ce lien maintenant',
    onceHint: 'Il n’est affiché qu’une fois. Ouvrez-le sur l’écran : celui-ci reste connecté jusqu’à ce que vous révoquiez le lien. S’il est perdu, révoquez-le et créez-en un autre.',
    copy: 'Copier',
    copied: 'Copié',
    active: 'Liens de kiosque actifs',
    none: 'Aucun lien de kiosque pour l’instant.',
    created: 'Créé {{when}}',
    used: 'dernière utilisation {{when}}',
    neverUsed: 'jamais utilisé',
    revoke: 'Révoquer',
    revokeConfirm: 'Révoquer ce lien de kiosque ? Les écrans qui l’utilisent cessent aussitôt d’afficher les données.',
    limit: 'Votre organisation a déjà 50 liens de kiosque : révoquez-en un d’abord.',
  },
  settings: {
    organisation: 'Organisation',
    members: 'Équipe',
    sites: 'Sites et équipements',
    rules: 'Règles d’alerte',
    escalation: 'Escalade',
    notifications: 'Mes notifications',
    exports: 'Exports',
    audit: 'Journal d’audit',
    billing: 'Abonnement',
    haccp: 'HACCP',
    kiosks: 'Kiosques',
  },
  billing: {
    state: {
      trialing: 'Essai gratuit',
      active: 'Actif',
      past_due: 'Paiement en échec',
      restricted: 'Lecture seule',
      canceled: 'Terminé',
    },
    price: '{{amount}} par {{interval}}, HT',
    interval: { month: 'mois', year: 'an' },
    trialLocal: 'Votre essai gratuit se termine le {{date}}. Choisissez une formule pour continuer la surveillance sans interruption.',
    trialCarried: 'Le reste de votre essai est conservé : le premier paiement a lieu le {{date}}.',
    trialStripe: 'Gratuit jusqu’au {{date}}, puis prélevé automatiquement.',
    renews: 'Renouvellement le {{date}}.',
    endsOn: 'Se termine le {{date}} ; il ne sera pas renouvelé.',
    scheduledChange: 'Un changement de formule prend effet à la fin de la période.',
    noPaymentMethod: 'Aucun moyen de paiement enregistré : ajoutez-en un dans l’espace de facturation.',
    pastDue: 'Le dernier paiement a échoué. Mettez à jour le moyen de paiement avant le {{date}} ; passé cette date, le compte passe en lecture seule.',
    restricted: 'Le compte est en lecture seule : les modifications sont bloquées et les alertes ne partent plus que par e-mail. Sans règlement d’ici le {{date}}, l’abonnement est résilié.',
    restrictedTrial: 'Votre essai est terminé et le compte est en lecture seule. Choisissez une formule pour tout retrouver ; sans abonnement d’ici le {{date}}, le compte est fermé.',
    canceled: 'L’abonnement s’est terminé le {{date}}.',
    ingestionUntil: 'La surveillance de vos appareils s’arrête le {{date}} ; d’ici là, les alertes ne partent que par e-mail.',
    ingestionStopped: 'La surveillance de vos appareils s’est arrêtée le {{date}}.',
    erasureAfter: 'Vos données sont conservées jusqu’au {{date}}, puis effacées. Exportez-les avant si vous en avez besoin, ou réabonnez-vous pour les garder.',
    manage: 'Factures, paiement et formule',
    manageHint: 'L’espace de facturation (Stripe) : factures, moyen de paiement, numéro de TVA, changement de formule et résiliation.',
    choose: 'Choisir une formule',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    yearlySaving: '−{{pct}} %',
    subscribe: 'S’abonner',
    current: 'Formule actuelle',
    limitSites_one: '{{count}} site',
    limitSites_other: '{{count}} sites',
    limitDevices_one: '{{count}} appareil',
    limitDevices_other: '{{count}} appareils',
    limitUsers_one: '{{count}} utilisateur',
    limitUsers_other: '{{count}} utilisateurs',
    over: 'Votre utilisation dépasse cette formule : {{items}}.',
    overItem: '{{used}} {{what}} pour {{max}}',
    changeInPortal: 'Pour changer de formule, ouvrez l’espace de facturation : il propose les formules adaptées à votre utilisation.',
    returned: 'Paiement reçu : votre abonnement est en cours d’activation…',
    paymentReturned: 'Paiement reçu : votre abonnement est en cours de mise à jour…',
    unavailable: 'Le paiement en ligne n’est pas disponible ici. Contactez-nous pour vous abonner.',
    providerDown: 'Le service de paiement ne répond pas. Réessayez dans un instant.',
    priceVat: '{{amount}} par {{interval}}, TTC',
    exclVat: 'HT',
    inclVat: 'TTC',
    paidWith: 'Payé via {{provider}}.',
    provider: { stripe: 'Stripe', revolut: 'Revolut' },
    payWith: 'Paiement',
    payWithHint: {
      stripe: 'Par carte, via Stripe. Prix hors taxes, la TVA est ajoutée au paiement ; les factures sont dans l’espace de facturation.',
      revolut: 'Avec Revolut Pay ou une carte, via Revolut. Prix TTC ; vos paiements sont listés sur cette page.',
    },
    scheduledPlan: 'Passe à la formule {{plan}} le {{date}}.',
    pastDueRevolut: 'Le dernier renouvellement a échoué. Réglez-le avant le {{date}} ; passé cette date, le compte passe en lecture seule.',
    revolut: 'Votre abonnement Revolut',
    revolutHint: 'Revolut prélève chaque renouvellement sur la carte utilisée au premier paiement. Un changement de formule prend effet au renouvellement suivant.',
    payNow: 'Régler le renouvellement',
    cancel: 'Résilier l’abonnement',
    cancelTitle: 'Résilier l’abonnement ?',
    cancelBody: 'Il se termine le {{date}}, à la fin de la période déjà payée ; d’ici là, tout fonctionne comme aujourd’hui. Vos données sont ensuite conservées 90 jours.',
    cancelNowBody: 'Le renouvellement impayé est abandonné et l’abonnement se termine maintenant : le compte passe en lecture seule, et vos données sont conservées 90 jours.',
    cancelConfirm: 'Résilier',
    keep: 'Conserver l’abonnement',
    change: 'Changer de formule',
    changeHint: 'La nouvelle formule et son prix s’appliquent au prochain renouvellement, le {{date}}.',
    switch: 'Changer au renouvellement',
    keepPlan: 'Garder cette formule',
    from: 'À partir du {{date}}',
    changeScheduled: 'La formule {{plan}} s’applique à partir du {{date}}.',
    payments: 'Paiements',
    paymentsEmpty: 'Aucun paiement pour l’instant.',
    paymentDate: 'Date',
    paymentFor: 'Objet',
    paymentPeriod: 'Période',
    paymentAmount: 'Montant',
    paymentReason: { setup_intent: 'Premier paiement', cycle_billing: 'Renouvellement', other: 'Paiement' },
    amountVat: '{{amount}} ({{net}} HT)',
  },
  orgSettings: {
    general: 'Général',
    name: 'Nom',
    legalName: 'Raison sociale',
    sirenHint: 'Neuf chiffres, pour les factures.',
    vat: 'Numéro de TVA',
    timezone: 'Fuseau horaire par défaut',
    timezoneHint: 'Pour les nouveaux sites, les rapports et les exports.',
    language: 'Langue par défaut',
    languageHint: 'Pour les invitations et les contacts externes.',
    security: 'Sécurité',
    require2fa: 'Vérification en deux étapes',
    require2faHint: 'Les membres qui ne l’ont pas activée doivent le faire avant de continuer.',
    ownerOnly: 'Seul un titulaire peut modifier ce réglage.',
    require2faNone: 'Facultative',
    require2faAdmins: 'Obligatoire pour les titulaires et administrateurs',
    require2faAll: 'Obligatoire pour tous',
    enable2faFirst: 'Activez d’abord la vérification en deux étapes sur votre propre compte.',
    supportAccess: 'Autoriser notre assistance à consulter ce compte lorsque vous demandez de l’aide (chaque accès est enregistré).',
    usage: 'Utilisation de la formule — {{plan}}',
    quotaPeriod: 'Messages comptés depuis le {{date}}.',
    danger: 'Effacer l’organisation',
    erasureScheduled: 'Effacement prévu le {{date}}.',
    cancelErasure: 'Annuler l’effacement',
    erasureHint: 'Tous les sites, équipements, mesures et registres sont effacés au bout de 7 jours. Téléchargez d’abord vos exports.',
    erase: 'Effacer l’organisation',
    eraseConfirm: 'C’est irréversible une fois les 7 jours écoulés. Saisissez « {{name}} » pour confirmer.',
    typeName: 'Nom de l’organisation',
  },
  usage: {
    sites: 'Sites',
    assets: 'Équipements',
    devices: 'Boîtiers',
    probes: 'Sondes',
    users: 'Utilisateurs',
    sms: 'SMS ce mois-ci',
    whatsapp: 'WhatsApp ce mois-ci',
  },
  members: {
    invite: 'Inviter quelqu’un',
    member: 'Membre',
    role: 'Rôle',
    twoFactor: 'Deux étapes',
    since: 'Depuis',
    leave: 'Quitter',
    leaveConfirm: 'Quitter cette organisation ? Il vous faudra une nouvelle invitation pour revenir.',
    remove: 'Retirer',
    removeConfirm: 'Retirer {{name}} de l’organisation ?',
    lastOwner: 'Une organisation doit avoir au moins un titulaire : désignez d’abord un autre titulaire.',
    limit: 'La limite d’utilisateurs de votre formule est atteinte. Changez de formule pour en ajouter.',
    already: 'Cette personne est déjà membre.',
    pending: 'Invitations en attente',
    expires: 'Expire',
    revoke: 'Révoquer',
    send: 'Envoyer l’invitation',
  },
  structure: {
    subtitle: 'Chaque site a son propre fuseau horaire : ses relevés journaliers et ses rapports le suivent.',
    addSite: 'Ajouter un site',
    editSite: 'Modifier le site',
    firstSite: 'Créez votre premier site, puis ajoutez-y ses équipements.',
    siteName: 'Nom du site',
    address: 'Adresse',
    timezoneHint: 'Celui du lieu où se trouve le site.',
    archive: 'Archiver',
    archiveSiteConfirm: 'Archiver le site « {{name}} » ? Son registre est conservé.',
    siteInUse: 'Archivez ou déplacez d’abord les équipements de ce site.',
    addAsset: 'Ajouter un équipement',
    editAsset: 'Modifier l’équipement',
    assetName: 'Nom',
    type: 'Type',
    site: 'Site',
    notes: 'Notes',
    applyTemplate: 'Ajouter les règles d’alerte recommandées',
    archiveAssetConfirm: 'Archiver « {{name}} » ? Ses sondes sont désaffectées et son registre est conservé.',
    limit: 'La limite de votre formule est atteinte. Changez de formule pour en ajouter.',
    tariff: 'Tarif',
    ambientProbe: 'Sonde de température de la pièce',
    ambientProbeHint: 'Facultatif : une sonde qui mesure la pièce autour des groupes froids, pour comparer leur consommation à température égale.',
    noAmbientProbe: 'Aucune',
    volume: 'Volume utile (litres)',
    volumeHint: 'Facultatif : pour comparer consommation et coût par litre.',
    volumeInvalid: 'Saisissez un volume en litres, ou laissez vide.',
    rangeInvalid: 'Le maximum doit être au-dessus du minimum.',
    location: 'Emplacement',
    locationHint: 'Pour la carte de vos sites : cliquez sur la carte, déplacez l’épingle ou saisissez les coordonnées.',
    latitude: 'Latitude',
    longitude: 'Longitude',
    clearLocation: 'Retirer l’emplacement',
    locationInvalid: 'Saisissez une latitude (−90 à 90) et une longitude (−180 à 180) en degrés décimaux, ou laissez les deux vides.',
    pin: 'L’emplacement du site',
    onMap: 'Sur la carte',
    notOnMap: 'Sans emplacement',
  },
  templates: {
    fridge: 'Avertissement au-dessus de 5 °C pendant 30 min, critique au-dessus de 8 °C pendant 15 min, avertissement sous 0 °C pendant 1 h, porte ouverte 5 min.',
    freezer: 'Avertissement au-dessus de −15 °C pendant 45 min, critique au-dessus de −12 °C pendant 30 min, porte ouverte 3 min.',
    cold_room: 'Avertissement au-dessus de 5 °C pendant 30 min, critique au-dessus de 8 °C pendant 15 min, avertissement sous 0 °C pendant 1 h, porte ouverte 10 min.',
    blast_freezer: 'Critique au-dessus de −10 °C pendant 90 min, porte ouverte 10 min.',
    truck: 'Avertissement au-dessus de 5 °C pendant 30 min, critique au-dessus de 8 °C pendant 15 min, avertissement sous 0 °C pendant 1 h, porte ouverte 15 min.',
    other: 'D’après la plage saisie : avertissement au-dessus du maximum pendant 30 min, critique 3 °C au-dessus pendant 15 min, avertissement sous le minimum pendant 1 h. Sans plage, aucune alerte : ajoutez-les vous-même.',
  },
  rules: {
    subtitle: 'Quand une alarme est levée, et pour qui. Chaque modification est conservée pour le registre.',
    appliesTo: 'S’applique à',
    assets: 'Équipements',
    devices: 'Boîtiers',
    noScope: 'Ajoutez d’abord un équipement ou un boîtier.',
    list: 'Règles',
    add: 'Ajouter une règle',
    edit: 'Modifier la règle',
    none: 'Aucune règle : aucune alarme ne sera levée.',
    rule: 'Règle',
    condition: 'Condition',
    state: 'État',
    oneProbe: 'une seule sonde',
    enabled: 'Active',
    disabled: 'Inactive',
    enable: 'Activer',
    disable: 'Désactiver',
    history: 'Historique',
    archiveConfirm: 'Archiver cette règle ? Son historique est conservé.',
    stale: 'Quelqu’un vient de modifier cette règle. La page a été actualisée : vérifiez puis réessayez.',
    type: 'Type',
    severity: 'Gravité',
    severityHint: 'Les alarmes critiques peuvent passer outre les heures calmes.',
    threshold: 'Seuil',
    holdMin: 'Pendant au moins (minutes)',
    holdHint: 'Les pics brefs (porte ouverte, livraison) ne lèvent pas d’alarme.',
    hysteresis: 'Marge de retour à la normale',
    hysteresisHint: '°C du bon côté du seuil avant de considérer le retour à la normale.',
    clearDelayMin: 'Retour à la normale pendant (minutes)',
    delta: 'Variation de',
    windowMin: 'En (minutes)',
    direction: 'Sens',
    dir: {
      rising: 'Hausse',
      falling: 'Baisse',
      either: 'Les deux',
    },
    afterMin: 'Au bout de (minutes)',
    doorHint: 'Porte ouverte plus longtemps que cela.',
    offlineHint: 'Aucune nouvelle du boîtier pendant plus longtemps que cela.',
    batteryV: 'Sous (volts)',
    policy: 'Escalade',
    policyHint: 'Qui est alerté, et dans quel ordre.',
    policyDefault: 'Celle de l’organisation par défaut',
    name: 'Nom',
    nameHint: 'Facultatif, par ex. « Vitrine poisson ».',
    versionHint: 'L’enregistrement crée une nouvelle version : les alarmes gardent la version sous laquelle elles ont été levées.',
    historyTitle: 'Historique de la règle',
    version: 'Version',
    inForce: 'En vigueur',
    changedBy: 'Modifiée par',
    now: 'maintenant',
    summaryHigh: 'au-dessus de {{value}} pendant {{hold}}',
    summaryLow: 'en dessous de {{value}} pendant {{hold}}',
    summaryRate: 'varie de {{delta}} en {{window}}',
    summaryAfter: 'au bout de {{after}}',
    summaryBattery: 'sous {{value}} V pendant {{hold}}',
    summaryHold: 'pendant {{hold}}',
    summaryCooling: 'de {{start}} à {{target}} en {{minutes}} min (réglages HACCP)',
    meters: 'Compteurs d’énergie',
    energyHint: 'Les règles des compteurs surveillent l’électricité de l’équipement : un appareil qui ne consomme plus (disjoncteur, débranché), un compresseur qui ne s’arrête plus ou démarre trop souvent, un compteur qui ne remonte plus de données.',
    minW: 'En dessous de (W)',
    minWHint: 'Un réfrigérateur en service consomme davantage, même entre deux cycles.',
    noConsumptionHint: 'Plus longtemps que cela : l’équipement n’est plus alimenté.',
    maxOnMin: 'En marche depuis plus de (minutes)',
    maxOnHint: 'Un compresseur qui ne s’arrête plus : fuite, givre ou porte restée ouverte.',
    maxStarts: 'Plus de (démarrages par heure)',
    maxStartsHint: 'Les cycles courts usent le compresseur.',
    meterOfflineHint: 'Aucune donnée du compteur pendant plus longtemps que cela.',
    summaryNoConsumption: 'sous {{w}} W pendant {{hold}}',
    summaryContinuous: 'en marche plus de {{hold}}',
    summaryShortCycling_one: 'plus de {{count}} démarrage par heure',
    summaryShortCycling_other: 'plus de {{count}} démarrages par heure',
  },
  mutes: {
    title: 'Pauses',
    add: 'Mettre en pause',
    hint: 'Pendant un dégivrage, un nettoyage ou une livraison. Les mesures restent enregistrées, et les dépassements figurent toujours au registre.',
    kind: 'Quand',
    adhoc: 'Dès maintenant',
    scheduled: 'Chaque semaine',
    mode: 'Mode',
    suppress: 'Aucune alarme',
    relax: 'Relever le seuil',
    modeHint: {
      suppress: 'Aucune alarme n’est levée pendant la pause.',
      relax: 'Les alarmes ne sont levées qu’au-dessus d’un seuil plus élevé.',
    },
    relaxThreshold: 'Seuil pendant la pause',
    hours: 'Pendant (heures)',
    maxAdmin: 'Jusqu’à 72 heures.',
    maxOperator: 'Jusqu’à 4 heures.',
    days: 'Jours',
    start: 'À partir de',
    startHint: 'Heure locale du site.',
    durationMin: 'Pendant (minutes)',
    reason: 'Motif',
    none: 'Aucune pause.',
    until: 'jusqu’au {{date}}',
    every: 'chaque {{days}} à {{start}} pendant {{duration}}',
    cancel: 'Terminer',
  },
  weekday: {
    '1': 'lun.',
    '2': 'mar.',
    '3': 'mer.',
    '4': 'jeu.',
    '5': 'ven.',
    '6': 'sam.',
    '7': 'dim.',
  },
  escalation: {
    subtitle: 'Qui est alerté, et quand l’alerte passe à la personne suivante si personne ne l’acquitte.',
    addPolicy: 'Ajouter une politique',
    editPolicy: 'Modifier la politique',
    default: 'Par défaut',
    deleteConfirm: 'Supprimer cette politique ? Les règles qui l’utilisent passent à celle par défaut.',
    inUse: 'Des règles utilisent encore cette politique.',
    contactInUse: 'Des politiques alertent encore ce contact.',
    noLevels: 'Personne n’est alerté par cette politique.',
    levelN: 'Niveau {{n}}',
    immediately: 'immédiatement',
    after: 'au bout de {{delay}}',
    reminders: 'Rappel toutes les {{every}} tant que l’alarme n’est pas acquittée.',
    contacts: 'Contacts externes',
    addContact: 'Ajouter un contact',
    editContact: 'Modifier le contact',
    contactsHint: 'Des personnes sans compte : un frigoriste, une société de télésurveillance.',
    noContacts: 'Aucun contact externe.',
    contactName: 'Nom',
    reach: 'Joint par',
    external: 'externe',
    policyName: 'Nom',
    wholeOrg: 'Toute l’organisation',
    reminderMin: 'Rappel toutes les (minutes)',
    reminderHint: 'Vide : pas de rappel.',
    delayMin: 'Au bout de (minutes)',
    delayHint: 'Après l’alarme, si personne ne l’a acquittée.',
    recipient: 'Destinataire',
    channel: 'Canal',
    theirChannels: 'Ses canaux préférés',
    addRecipient: 'Ajouter un destinataire',
    removeLevel: 'Supprimer le niveau',
    addLevel: 'Ajouter un niveau',
    channels: 'Canaux',
  },
  notifications: {
    subtitle: 'Comment vous êtes alerté pour cette organisation. Le premier canal disponible est utilisé.',
    up: 'Monter',
    down: 'Descendre',
    notSetUp: 'Configurez-le d’abord dans votre profil.',
    notInPlan: 'Non inclus dans la formule.',
    quiet: 'Heures calmes',
    quietStart: 'De',
    quietEnd: 'À',
    criticalOverrides: 'Les alarmes critiques passent outre les heures calmes',
    onClear: 'Me prévenir quand une température revient à la normale',
    hint: 'Un e-mail est toujours envoyé en plus, pour la trace.',
  },
  exports: {
    subtitle: 'Les mesures en fichiers CSV, pour vos archives ou un contrôle. Les fichiers sont conservés 7 jours.',
    new: 'Nouvel export',
    last7: '7 derniers jours',
    last30: '30 derniers jours',
    lastMonth: 'Mois dernier',
    from: 'Du',
    to: 'Au',
    inclusive: 'Inclus.',
    tier: 'Détail',
    maxDays: 'Jusqu’à {{days}} jours par export.',
    tiers: {
      raw: 'Toutes les mesures',
      '5m': 'Moyennes sur 5 minutes',
      '1h': 'Moyennes horaires',
      local_day: 'Minimum, maximum et moyenne par jour',
    },
    assets: 'Équipements',
    assetsAll: 'Tous les équipements.',
    assetsSome: 'Équipements choisis : {{n}}.',
    clear: 'Tous les équipements',
    timezone: 'Jours et heures en {{tz}}.',
    create: 'Préparer le fichier',
    toBeforeFrom: 'La fin précède le début.',
    tooLong: 'Trop long pour ce détail : {{days}} jours au plus.',
    tooMany: 'Trois exports sont déjà en préparation. Attendez qu’un se termine.',
    recent: 'Exports récents',
    none: 'Aucun export pour l’instant.',
    requested: 'Demandé',
    period: 'Période',
    state: 'État',
    size: 'Taille',
    download: 'Télécharger',
    until: 'jusqu’au {{date}}',
    energy: {
      title: 'Rapport énergie',
      hint: 'Un mois de consommation et de coût par équipement, avec sa dérive et les comparaisons du mois : pour l’organisation, un site ou un équipement.',
      month: 'Mois',
      scope: 'Pour',
      all: 'Toute l’organisation',
      sites: 'Sites',
      format: 'Format',
      pdfNotInPlan: 'PDF avec une formule supérieure.',
      queued: 'Le rapport est en préparation : il apparaîtra ci-dessous.',
    },
    kind: {
      csv_readings: 'Mesures CSV',
      haccp_pdf: 'Rapport HACCP',
      haccp_csv: 'Registre HACCP (CSV)',
      gdpr_export: 'Export complet',
      maintenance_csv: 'Rapport de maintenance (CSV)',
      maintenance_pdf: 'Rapport de maintenance (PDF)',
      energy_csv: 'Rapport énergie (CSV)',
      energy_pdf: 'Rapport énergie (PDF)',
    },
    states: {
      queued: 'En attente',
      running: 'En préparation',
      succeeded: 'Prêt',
      failed: 'Échec',
      expired: 'Expiré',
    },
  },
  audit: {
    subtitle: 'Toutes les modifications faites dans cette organisation. Le journal est chaîné : il ne peut pas être altéré sans que cela se voie.',
    group: 'Type d’action',
    allActions: 'Toutes les actions',
    actorFilter: 'Par',
    everyone: 'Tout le monde',
    at: 'Quand',
    who: 'Qui',
    action: 'Action',
    details: 'Détails',
    none: 'Aucune entrée.',
    formerMember: 'Ancien membre',
    staffAs: 'Assistance, en tant que {{name}}',
    actor: {
      user: 'Membre',
      staff: 'Assistance',
      system: 'Système',
      device: 'Boîtier',
    },
    groups: {
      alarm: 'Alarmes',
      alarm_rule: 'Règles d’alerte',
      annotation: 'Remarques',
      asset: 'Équipements',
      contact: 'Contacts',
      device: 'Boîtiers',
      escalation_policy: 'Escalade',
      export: 'Exports',
      invitation: 'Invitations',
      membership: 'Équipe',
      mute_window: 'Pauses',
      organization: 'Organisation',
      probe: 'Sondes',
      site: 'Sites',
      support: 'Assistance',
    },
  },
  profile: {
    title: 'Mon profil',
    details: 'Informations',
    email: 'E-mail',
    verified: 'Confirmée',
    unverified: 'Non confirmée',
    name: 'Nom',
    language: 'Langue',
    languageHint: 'Du tableau de bord et des alertes que vous recevez.',
    timezoneHint: 'Pour les heures indiquées dans vos alertes.',
    password: 'Mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmez le mot de passe',
    passwordMismatch: 'Les deux mots de passe diffèrent.',
    passwordOthers: 'Vos autres sessions seront déconnectées.',
    changePassword: 'Changer le mot de passe',
    passwordChanged: 'Mot de passe modifié. Vos autres sessions ont été déconnectées.',
    twoFactor: 'Vérification en deux étapes',
    on: 'Activée',
    off: 'Désactivée',
    twoFactorIntro: 'À la connexion, un code d’application d’authentification (Google Authenticator, Microsoft Authenticator, 1Password…) est demandé après le mot de passe.',
    enable2fa: 'Activer la vérification en deux étapes',
    qrAlt: 'QR code à scanner avec l’application d’authentification',
    step1: 'Scannez ce QR code avec votre application d’authentification.',
    step2: 'Saisissez le code à 6 chiffres qu’elle affiche.',
    manualKey: 'Impossible de scanner ? Saisissez cette clé :',
    code: 'Code',
    activate: 'Activer',
    wrongCode: 'Code incorrect. Vérifiez l’heure de votre téléphone et réessayez.',
    recoveryTitle: 'Conservez vos codes de secours',
    recoveryHint: 'Chaque code sert une fois, si vous perdez votre téléphone. Gardez-les en lieu sûr : ils ne seront plus affichés.',
    copy: 'Copier',
    download: 'Télécharger',
    savedCodes: 'Je les ai conservés',
    regenerate: 'Nouveaux codes de secours',
    regenerateConfirm: 'Remplacer vos codes de secours ? Les codes actuels cesseront de fonctionner.',
    disable2fa: 'Désactiver',
    disable2faConfirm: 'Désactiver la vérification en deux étapes ? Votre compte ne sera plus protégé que par le mot de passe.',
    twoFactorOff: 'La vérification en deux étapes est désactivée.',
    requiredByOrg: 'Une organisation dont vous êtes membre l’exige : {{detail}}.',
    phone: 'Téléphone mobile',
    noPhone: 'Aucun numéro pour l’instant : ajoutez-en un pour recevoir les alertes par SMS ou WhatsApp.',
    addPhone: 'Numéro de mobile',
    newPhone: 'Nouveau numéro',
    phoneHint: 'Au format international, par ex. +33 6 12 34 56 78.',
    sendCode: 'Envoyer un code',
    smsCode: 'Code envoyé par SMS au {{phone}}',
    verify: 'Confirmer',
    phoneVerified: 'Numéro confirmé.',
    phoneConfirmed: 'Confirmé',
    phoneUnconfirmed: 'Non confirmé',
    unreachable: 'Aucun SMS ne peut être envoyé à ce numéro.',
    channels: 'Messageries',
    channelsIntro: 'Où vous pouvez recevoir les alertes. Choisissez l’ordre par organisation dans « Mes notifications ».',
    connected: 'Connecté',
    phoneFirst: 'Confirmez d’abord un numéro de mobile.',
    whatsappHint: 'Les alertes sur WhatsApp, à votre numéro confirmé.',
    optIn: 'Recevoir les alertes',
    disconnect: 'Déconnecter',
    telegramHint: 'Les alertes de notre bot Telegram.',
    telegramPending: 'Ouvrez le bot et appuyez sur Démarrer (lien valable jusqu’à {{time}}).',
    openTelegram: 'Ouvrir Telegram',
    telegramUnavailable: 'Telegram n’est pas disponible sur ce service.',
    connect: 'Connecter',
    sessions: 'Où vous êtes connecté',
    device: 'Appareil',
    lastSeen: 'Dernière activité',
    signedIn: 'Connecté le',
    thisDevice: 'Cet appareil',
    signOutSession: 'Déconnecter',
  },
  maintenance: {
    tab: 'Maintenance',
    tabTemperatures: 'Températures',
    notInPlan: 'Le suivi de maintenance n’est pas inclus dans votre formule',
    notInPlanHint: 'Changez de formule dans Paramètres → Abonnement pour enregistrer les interventions et suivre les coûts.',
    disclaimer: 'Les valeurs comptables et l’amortissement sont calculés pour l’aide à la décision opérationnelle. Ce ne sont pas des chiffres comptables officiels.',
    thresholdReached: 'Seuil atteint',
    thresholdNear: 'Proche du seuil',
    thresholdOk: 'Sous le seuil',
    thresholdReachedTitle: 'La maintenance coûte désormais plus que la valeur de l’équipement',
    thresholdReachedBody: 'Coût de maintenance {{basis}} : {{cost}}, pour une valeur comptable de {{value}}. Envisagez son remplacement.',
    basis: { rolling_12m: 'sur les 12 derniers mois', cumulative: 'depuis l’achat' },
    basisLong: { rolling_12m: '12 derniers mois (recommandé)', cumulative: 'Tout depuis l’achat' },
    bookValue: 'Valeur comptable',
    age: '{{years}} ans',
    noProfileShort: 'Pas de profil financier',
    cumulative: 'Coût cumulé',
    perYear: '{{value}} par année d’âge',
    rolling: 'Coût sur 12 mois',
    interventionsLast12_one: '{{count}} intervention en 12 mois',
    interventionsLast12_other: '{{count}} interventions en 12 mois',
    mtbf: 'Temps moyen entre pannes',
    days_one: '{{count}} jour',
    days_other: '{{count}} jours',
    trend: { rising: 'Interventions en hausse', stable: 'Interventions stables', falling: 'Interventions en baisse' },
    threshold: 'Seuil de remplacement',
    needsProfile: 'Ajoutez le profil financier pour comparer les coûts à la valeur comptable.',
    thresholdLine: 'Coût {{basis}} face à {{margin}} % de la valeur comptable',
    projection: 'Franchissement projeté',
    projectionNotInPlan: 'Les projections sont incluses dans les formules Pro et Multisite.',
    crossingOn: 'Vers le {{date}}',
    range: 'Entre le {{from}} et le {{to}} (intervalle à 95 %)',
    rangeOpen: 'Au plus tôt le {{from}}',
    status: {
      exceeded: 'Le seuil est déjà atteint.',
      not_within_horizon: 'Pas attendu d’ici 15 ans à la tendance actuelle.',
      insufficient_data: 'Historique insuffisant : il faut trois interventions ou douze mois.',
    },
    statusShort: { exceeded: 'Atteint', projected: 'Projeté', not_within_horizon: 'Au-delà de 15 ans', insufficient_data: 'Historique insuffisant' },
    model: { linear: 'Tendance linéaire', exponential: 'Tendance accélérée (exponentielle)' },
    points_one: '{{count}} point',
    points_other: '{{count}} points',
    thresholdSeries: 'Seuil',
    date: 'Date',
    crossingShort: 'Franchissement ~ {{date}}',
    crossedShort: 'Seuil franchi le {{date}}',
    chartTitle: 'Valeur comptable et coût de maintenance',
    chartHint: 'En pointillés : la projection à la tendance actuelle. En grisé : l’intervalle du franchissement projeté.',
    profile: 'Profil financier',
    addProfile: 'Ajouter',
    noProfile: 'Pas encore de profil financier : ajoutez l’achat et l’amortissement pour suivre la valeur comptable.',
    purchaseDate: 'Date d’achat',
    purchaseValue: 'Valeur d’achat',
    purchaseValueHt: 'Valeur d’achat HT',
    method: 'Amortissement',
    methods: { straight_line: 'Linéaire', declining_balance: 'Dégressif' },
    usefulLife: 'Durée d’utilité',
    usefulLifeYears: 'Durée d’utilité (ans)',
    years_one: '{{count}} an',
    years_other: '{{count}} ans',
    residual: 'Valeur résiduelle',
    decliningRate: 'Taux annuel (%)',
    decliningDefault: 'Par défaut : le double du taux linéaire',
    warrantyEnd: 'Garantie jusqu’au',
    supplier: 'Fournisseur',
    brand: 'Marque',
    model_: 'Modèle',
    brandModel: 'Marque et modèle',
    serial: 'N° de série',
    schedules: 'Maintenance préventive',
    addSchedule: 'Planifier',
    scheduleName: 'Quoi',
    scheduleNamePlaceholder: 'Révision du compresseur, nettoyage du condenseur…',
    everyMonths: 'Tous les (mois)',
    firstDue: 'Première échéance',
    remindDays: 'Rappel (jours avant)',
    noSchedules: 'Aucune maintenance préventive planifiée.',
    every_one: 'Tous les mois',
    every_other: 'Tous les {{count}} mois',
    due: 'échéance le {{date}}',
    lastDone: 'faite le {{date}}',
    scheduleStatus: { ok: 'À jour', due_soon: 'Bientôt', overdue: 'En retard' },
    archive: 'Retirer',
    log: 'Journal de maintenance',
    logHint: 'Les entrées ne sont jamais modifiées : une correction, une note ou une annulation s’ajoute à l’entrée et figure au journal d’audit.',
    addIntervention: 'Enregistrer une intervention',
    invalidAmount: 'Vérifiez les montants : en euros, par exemple 120,50.',
    type: 'Type',
    types: { preventive: 'Préventive', corrective: 'Corrective', emergency: 'Urgence' },
    provider: 'Prestataire',
    description: 'Ce qui a été fait',
    parts: 'Pièces (HT)',
    labour: 'Main-d’œuvre (HT)',
    travel: 'Déplacement (HT)',
    downtime: 'Arrêt (minutes)',
    linkedAlarm: 'Alarme de température',
    linkedAlarmHint: 'Si l’intervention répond à une alarme (panne de compresseur par exemple), elle figure aussi dans l’historique de l’alarme.',
    noAlarm: 'Aucune',
    fulfils: 'Réalise la maintenance planifiée',
    invoice: 'Facture (PDF ou photo)',
    photo: 'Photo',
    total: 'Total',
    noInterventions: 'Aucune intervention enregistrée.',
    corrected: 'Corrigée',
    voided: 'Annulée',
    openAlarm: 'Alarme liée',
    costLine: 'pièces {{parts}}, main-d’œuvre {{labour}}, déplacement {{travel}}',
    downtimeLine: '{{minutes}} min d’arrêt',
    loggedBy: 'par {{name}}',
    attachment: { invoice: 'Facture', photo: 'Photo' },
    annotationKind: { note: 'Note', correction: 'Correction', void: 'Annulation' },
    field: { performed_on: 'date', type: 'type', description: 'description', provider: 'prestataire', parts_cents: 'pièces',
      labour_cents: 'main-d’œuvre', travel_cents: 'déplacement', downtime_minutes: 'arrêt' },
    note: 'Note',
    correct: 'Corriger',
    void: 'Annuler',
    reason: { note: 'Note', correction: 'Motif de la correction', void: 'Motif de l’annulation' },
    confirm: { note: 'Ajouter la note', correction: 'Enregistrer la correction', void: 'Annuler l’entrée' },
    appendOnly: 'L’entrée d’origine reste au journal.',
    exportCsv: 'Exporter en CSV',
    exportPdf: 'Exporter en PDF',
    exportQueued: 'Le rapport est en préparation.',
    openExports: 'Voir les exports',
    fleetTitle: 'Maintenance',
    fleetSubtitle: 'Tous les équipements par urgence de remplacement : coût de maintenance face à la valeur comptable.',
    fleetNotInPlan: 'Le classement du parc est inclus dans les formules Pro et Multisite',
    fleetNotInPlanHint: 'Enregistrez les interventions dans l’onglet Maintenance de chaque équipement ; projections et classement avec une formule supérieure.',
    seePlans: 'Voir les formules',
    ranking: 'Urgence de remplacement',
    site: 'Site',
    allSites: 'Tous les sites',
    sortBy: 'Trier par',
    sortUrgency: 'Urgence',
    sortSite: 'Site',
    asset: 'Équipement',
    urgency: 'Urgence',
    basisCost: 'Coût de maintenance',
    crossing: 'Franchissement projeté',
    overdue: 'En retard',
    noAssets: 'Aucun équipement.',
    urgencyHint: 'L’urgence vaut 100 une fois le seuil atteint ; sinon elle croît à mesure que le coût approche du seuil et que le franchissement projeté approche. Mise à jour après chaque changement et chaque nuit.',
    settings: 'Seuil de remplacement et conseils',
    costBasis: 'Comparer',
    costBasisHint: 'Le coût de maintenance comparé à la valeur comptable.',
    margin: 'Alerter à (% de la valeur comptable)',
    marginHint: '100 : quand le coût atteint la valeur comptable ; 80 : plus tôt.',
    digest: 'Synthèse par e-mail',
    digestHint: 'Aux propriétaires et administrateurs : équipements proches du remplacement, maintenances en retard, dépenses par site.',
    digestNotInPlan: 'Avec les formules Pro et Multisite.',
    digestFrequency: { off: 'Désactivée', monthly: 'Mensuelle', quarterly: 'Trimestrielle' },
    advisoryHint: 'Les conseils sont envoyés aux propriétaires et administrateurs sur leurs canaux d’alerte, en semaine de 9 h à 18 h, jamais pendant leurs heures de silence.',
  },
  meters: {
    title: 'Compteur « {{meter}} »',
    settings: 'Réglages du compteur',
    model: 'Modèle',
    phases: 'Phases',
    lastData: 'Dernières données',
    noData: 'Pas encore de données',
    connection: 'Connexion',
    direct: 'Directe (sans boîtier)',
    viaGateway: 'Par le boîtier',
    host: 'Adresse sur le réseau local',
    hostHint: 'Seulement si le boîtier ne trouve pas le compteur tout seul (mDNS). Une adresse IP ou un nom d’hôte.',
    hostSeen: 'Trouvé par le boîtier à l’adresse {{host}}. Laissez vide pour la découverte automatique.',
    thresholds: 'Seuils du compresseur',
    learned: 'Appris à partir des données du compteur ({{date}}). Saisissez des valeurs pour les fixer vous-même.',
    manual: 'Fixés à la main ({{date}}).',
    learning: 'Pas encore appris : le compteur les apprend de ses premières heures de données. Vous pouvez aussi les fixer à la main.',
    onW: 'En marche au-dessus de (W)',
    onWHint: 'Le compresseur est considéré en marche au-dessus de cette puissance.',
    offW: 'À l’arrêt en dessous de (W)',
    offWHint: 'Facultatif ; au plus le seuil de marche.',
    defrostW: 'Dégivrage au-dessus de (W)',
    defrostWHint: 'Facultatif : la résistance de dégivrage consomme plus que le compresseur.',
    relearn: 'Oublier et réapprendre',
    relearnHint: 'Le compteur les réapprend de ses prochaines heures de données.',
    shelly: {
      add: 'Ajouter un Shelly sans boîtier',
      title: 'Ajouter un compteur Shelly sans boîtier',
      intro: 'Pour un site sans boîtier : le Shelly se connecte directement à la plateforme, par son WebSocket sortant. La plateforme ne fait que le lire ; elle ne le commute jamais.',
      quota: 'Compteurs utilisés : {{used}} sur {{max}} dans votre formule.',
      mac: 'Adresse MAC',
      macHint: 'Dans l’application Shelly ou sur son étiquette (12 caractères hexadécimaux).',
      modelHint: 'p. ex. S3PM-001PCEU16 (Plus Plug S), SPEM-003CEBEU (Pro 3EM).',
      single: 'Monophasé',
      three: 'Triphasé',
      channels: 'Voies',
      channelsHint: 'Circuits mesurés par ce Shelly (1 pour une prise).',
      submit: 'Ajouter le compteur',
      already: 'Ce Shelly est déjà enregistré.',
      limit: 'La limite de compteurs de votre formule est atteinte. Changez de formule pour en ajouter.',
      once: 'Copiez cette adresse maintenant',
      onceHint: 'Elle n’est affichée qu’une fois : elle contient la clé du compteur. Si elle est perdue, créez-en une nouvelle depuis la page de l’appareil.',
      address: 'Adresse de connexion',
      copy: 'Copier',
      copied: 'Copiée',
      step1: 'Ouvrez la page web du Shelly (son adresse IP) ou l’application Shelly, puis Paramètres → WebSocket sortant (Outbound WebSocket).',
      step2: 'Activez-le et collez l’adresse comme serveur.',
      step3: 'Laissez la vérification TLS activée (« Default TLS »), puis enregistrez : le Shelly se reconnecte tout seul.',
      step4: 'Dans la minute, l’appareil apparaît en ligne ici. Associez ensuite son compteur à l’équipement qu’il alimente.',
      readOnly: 'Recommandé : réglez le relais pour qu’il s’allume à la mise sous tension et verrouillez son bouton, pour que personne n’éteigne l’équipement par erreur.',
      openDevice: 'Ouvrir l’appareil',
      done: 'Terminé',
      newAddress: 'Nouvelle adresse',
      newAddressHint: 'Une nouvelle adresse de connexion est créée et l’actuelle cesse aussitôt de fonctionner : le Shelly est déconnecté jusqu’à ce que vous y saisissiez la nouvelle.',
      waiting: 'Saisissez son adresse de connexion dans le Shelly (Paramètres → WebSocket sortant). Si vous ne l’avez plus, créez-en une nouvelle.',
    },
  },
  energy: {
    tab: 'Énergie',
    disclaimer: 'Estimations à partir des compteurs et du tarif du site, pour comparer et décider : ce n’est pas une facture.',
    notInPlan: 'Le suivi énergétique n’est pas inclus dans votre formule',
    notInPlanHint: 'Mesurez ce que consomme et coûte chaque réfrigérateur et chambre froide, avec des compteurs d’énergie. Disponible avec une formule supérieure.',
    insightsNotInPlan: 'L’analyse énergétique n’est pas incluse dans votre formule',
    insightsNotInPlanHint: 'Consommation normale apprise par équipement, alertes de dérive avec causes probables, efficacité, comparaisons avant/après, classement du parc et retour sur investissement d’un remplacement : avec une formule supérieure.',
    noMeter: 'Aucun compteur d’énergie sur cet équipement',
    noMeterHint: 'Associez un compteur (un Shelly lu par le boîtier, ou connecté directement) à cet équipement depuis la page Appareils.',
    toDevices: 'Aller aux appareils',
    today: 'Aujourd’hui',
    last7: '7 derniers jours',
    last30: '30 derniers jours',
    monthToDate: 'Ce mois-ci',
    projectedAnnual: 'Projection annuelle',
    needs7Days: 'Après 7 jours complets',
    partial: '(données {{pct}} %)',
    chart: {
      title: 'Température et puissance',
      time: 'Heure',
      power: 'Puissance',
      defrost: 'Dégivrage',
      door: 'Porte ouverte',
      alarm: 'Alarme',
      maintenance: 'Intervention',
      tier: {
        minute: 'Puissance par minute.',
        hour: 'Puissance moyenne par heure.',
        day: 'Puissance moyenne par jour.',
      },
      statesHint: 'Les dégivrages s’affichent sur 8 jours au plus.',
    },
    cycles: {
      title: 'Cycles du compresseur',
      h24: '24 h',
      d7: '7 jours',
      duty: 'Temps de marche',
      perHour: 'Démarrages par heure',
      avgOn: 'Marche moyenne',
      avgOff: 'Arrêt moyen',
      longest: 'Plus longue marche',
      defrosts: 'Dégivrages',
      defrostShare: 'Énergie de dégivrage',
      runningW: 'Puissance en marche',
      none: 'Pas encore de cycle : le compteur apprend les seuils du compresseur de ses premières heures de données.',
      hint: 'Un compresseur qui tourne plus longtemps, démarre plus souvent ou consomme plus qu’à l’habitude est un signe à surveiller.',
    },
    drift: {
      title: 'Consommation normale et dérive',
      noBaseline: 'La consommation normale est apprise sur les premières semaines de données.',
      learning: 'Apprentissage de la consommation normale : {{days}} jours sur {{needed}}',
      learningHint: 'Depuis le {{date}} ; les jours incomplets ne comptent pas.',
      baseline: 'Normale : {{kwh}} kWh par jour (apprise depuis le {{date}}).',
      temperatureModel: 'Corrigée selon la température de la pièce.',
      status: {
        normal: 'Normale',
        drift: 'Consomme plus que d’habitude',
        insufficient_data: 'Pas assez de données cette semaine',
        no_baseline: 'Pas encore de référence',
      },
      statusShort: {
        normal: 'Normale',
        drift: 'Dérive',
        insufficient_data: 'Données insuffisantes',
        no_baseline: 'Apprentissage',
      },
      detail: '7 derniers jours : {{actual}} kWh pour {{expected}} kWh attendus (alerte au-delà de +{{threshold}} %).',
      conditioned: 'Comparé à température de pièce égale.',
      insufficientHint: 'Moins de 5 jours complets sur les 7 derniers.',
      causesTitle: 'Causes probables',
      because: 'Parce que :',
      causesHint: 'Des pistes tirées des données du compteur, pour orienter une vérification : seul un technicien peut les confirmer.',
      relearn: 'Réapprendre',
      relearnTitle: 'Réapprendre la consommation normale ?',
      relearnHint: 'Après une réparation, un remplacement ou un changement d’usage. La référence actuelle est abandonnée et une nouvelle est apprise sur les {{days}} prochains jours ; pas d’alerte de dérive entre-temps.',
    },
    cause: {
      dirty_condenser: {
        title: 'Condenseur encrassé ou ventilation gênée',
        text: 'Le compresseur consomme plus de puissance que d’habitude : il peine à évacuer la chaleur. Nettoyez le condenseur et vérifiez que rien ne gêne la circulation d’air autour du groupe.',
      },
      door_seal: {
        title: 'Joint de porte ou porte mal fermée',
        text: 'Le compresseur tourne plus longtemps sans plus d’ouvertures de porte : de l’air chaud entre. Vérifiez les joints et la fermeture de la porte.',
      },
      low_refrigerant: {
        title: 'Fuite de fluide ou compresseur fatigué',
        text: 'Le compresseur tourne presque en continu en consommant moins : il évacue moins de chaleur. Faites-le vérifier par un frigoriste.',
      },
      icing: {
        title: 'Givre sur l’évaporateur',
        text: 'Les dégivrages prennent une plus grande part de l’énergie : la glace s’accumule. Vérifiez les réglages de dégivrage, l’évacuation des eaux et les joints.',
      },
      usage: {
        title: 'Utilisation plus intense',
        text: 'La porte est ouverte plus souvent que d’habitude : plus d’air chaud et de produits à refroidir. Ce n’est pas une panne.',
      },
      ambient: {
        title: 'Pièce plus chaude',
        text: 'La pièce autour de l’équipement est plus chaude qu’au moment de l’apprentissage. Vérifiez la ventilation autour.',
      },
    },
    evidence: {
      running_w_up: 'plus de puissance en marche',
      running_w_down: 'moins de puissance en marche',
      duty_up: 'temps de marche plus long',
      near_continuous: 'tourne presque en continu',
      doors_unchanged: 'autant d’ouvertures de porte',
      doors_up: 'plus d’ouvertures de porte',
      defrost_up: 'plus d’énergie de dégivrage',
      ambient_up: 'pièce plus chaude',
    },
    efficiency: {
      title: 'Efficacité (30 jours)',
      perKelvin: 'Énergie par degré d’écart',
      perKelvinHint: 'Wh par jour et par °C entre la pièce et l’intérieur ; plus c’est bas, mieux c’est.',
      perDoor: 'Énergie par ouverture de porte',
      perDoorHint: 'Ce que coûte en plus chaque ouverture.',
      perM3: 'Énergie par m³ et par an',
      volume: 'Pour {{litres}} L utiles.',
      noVolume: 'Renseignez le volume dans les réglages de l’équipement.',
      perLitre: 'Coût par litre et par an',
      perLitreHint: 'Pour comparer des équipements de tailles différentes.',
    },
    tco: {
      title: 'Coût de possession',
      energy: 'Énergie',
      maintenance: 'Maintenance',
      total: 'Total',
      since: 'Énergie depuis le {{date}}, début du compteur ; toute la maintenance enregistrée.',
      noData: 'Pas encore de journée complète d’énergie.',
    },
    periods: {
      title: 'Consommation par période',
      by: 'Par',
      period: 'Période',
      energy: 'Énergie',
      cost: 'Coût',
      day: 'Jour (30 jours)',
      week: 'Semaine (6 mois)',
      month: 'Mois (12 mois)',
      year: 'Année',
      total: 'Total : {{kwh}}, {{cost}}.',
    },
    compare: {
      title: 'Avant et après une intervention',
      none: 'Aucune intervention enregistrée sur cet équipement : enregistrez-en une dans l’onglet Maintenance pour comparer.',
      intervention: 'Intervention',
      window: 'Jours comparés',
      daysEach_one: '± {{count}} jour',
      daysEach_other: '± {{count}} jours',
      before: 'Avant',
      after: 'Après',
      kwhDay: 'Énergie par jour',
      costDay: 'Coût par jour',
      ambient: 'Température de la pièce',
      days: 'Jours complets',
      change: 'Évolution',
      annualSavings: 'Économie par an',
      fewDays: 'Peu de jours complets : la comparaison est incertaine.',
      hint: 'Seuls les jours complets comptent. À température de pièce égale quand la consommation normale en tient compte.',
    },
    replace: {
      title: 'Remplacement',
      add: 'Comparer un appareil neuf',
      none: 'Saisissez un modèle de remplacement (d’après son étiquette énergie) pour voir ce qu’il ferait économiser et en combien de temps il serait rentabilisé.',
      label: 'Modèle',
      labelPlaceholder: 'p. ex. Marque X 600 L, classe C',
      kwhYear: 'kWh par an',
      kwhYearHint: 'D’après l’étiquette énergie.',
      price: 'Prix (HT)',
      class: 'Classe énergétique',
      classBadge: 'Classe {{cls}}',
      year: 'an',
      needsData: 'Les économies s’affichent après 7 jours complets de consommation.',
      annualSavings: 'Économie par an',
      split: 'Énergie {{energy}} + maintenance évitée {{maintenance}}',
      payback: 'Retour sur investissement',
      years: '{{value}} ans',
      never: 'Non rentabilisé',
      detail: 'Actuellement {{current}} kWh par an, l’appareil neuf {{candidate}} kWh par an, à {{rate}} centimes le kWh en moyenne. Maintenance évitée : celle des 12 derniers mois.',
      remove: 'Retirer',
    },
    fleetTitle: 'Énergie',
    fleetSubtitle: 'Ce que consomme et coûte chaque équipement, et ceux qui dérivent.',
    ranking: 'Équipements',
    sort: {
      cost: 'Coût',
      efficiency: 'Les moins efficaces',
      drift: 'Dérive',
      site: 'Site',
    },
    fleet: {
      kwh30: 'Énergie, 30 jours',
      cost30: 'Coût, 30 jours',
      assets: 'Équipements mesurés',
      last30: '30 jours',
      annual: 'Par an',
      perLitre: 'Par litre et par an',
      efficiency: 'Efficacité',
      drift: 'Dérive',
      duty: 'Marche, 7 jours',
      coverage: 'Données',
      none: 'Aucun équipement mesuré pour l’instant.',
      whDayK: '{{value}} Wh/j/°C',
      hint: 'Par an : projection à partir des jours complets des 30 derniers. Efficacité : kWh par an et par m³ utile, et Wh par jour et par °C d’écart. Mis à jour chaque nuit.',
      reports: 'Les rapports énergie mensuels (PDF ou CSV) se préparent depuis la page des exports.',
    },
    settings: {
      title: 'Réglages énergie',
      drift: 'Alerte de dérive au-delà de (%)',
      driftHint: 'Consommation des 7 derniers jours supérieure à la normale d’autant.',
      days: 'Période d’apprentissage (jours)',
      daysHint: 'Jours de données pour apprendre la consommation normale.',
      report: 'Rapport mensuel',
      reportHint: 'Envoyé par e-mail aux propriétaires et administrateurs au début de chaque mois.',
      on: 'Activé',
      off: 'Désactivé',
      meters: 'Compteurs utilisés : {{used}} sur {{max}} dans votre formule.',
    },
    tariff: {
      title: 'Tarif d’électricité — {{site}}',
      hint: 'Les prix du kWh (HT) du contrat du site, et son coût fixe mensuel. Chaque changement est une nouvelle version à partir de sa date : les coûts passés restent tels qu’ils étaient.',
      none: 'Aucun tarif : la consommation s’affiche sans son coût.',
      from: 'À partir du {{date}}',
      inForce: 'En vigueur',
      past: 'Passé',
      planned: 'Prévu',
      copy: 'Copier en nouvelle version',
      delete: 'Supprimer',
      deleteConfirm: 'Supprimer la version prévue à partir du {{date}} ?',
      add: 'Nouvelle version :',
      kind: {
        base: 'Base',
        hp_hc: 'Heures pleines / creuses',
        tempo: 'Tempo',
        custom: 'Personnalisé',
      },
      kindLabel: 'Type',
      label: 'Nom',
      labelPlaceholder: 'p. ex. Fournisseur X, 36 kVA',
      effectiveFrom: 'À partir du',
      effectiveHint: 'Une date passée recalcule le coût de la consommation depuis.',
      futureOnly: 'Une version prévue ne peut être déplacée qu’à une date future.',
      rates: 'Prix',
      centsUnit: 'centimes {{currency}} par kWh',
      rateName: 'Nom du tarif',
      rateNames: {
        BASE: 'Base',
        HP: 'Heures pleines',
        HC: 'Heures creuses',
        HP_BLEU: 'Bleu, HP',
        HC_BLEU: 'Bleu, HC',
        HP_BLANC: 'Blanc, HP',
        HC_BLANC: 'Blanc, HC',
        HP_ROUGE: 'Rouge, HP',
        HC_ROUGE: 'Rouge, HC',
      },
      addRate: 'Ajouter un prix',
      removeRate: 'Retirer ce prix',
      offpeakFrom: 'Heures creuses de',
      offpeakTo: 'Heures creuses jusqu’à',
      offpeakHint: 'Les heures de votre contrat ; elles peuvent passer minuit.',
      dayStart: 'Le jour tarifaire commence à',
      dayStartHint: 'Tempo : 06:00.',
      tempoHint: 'La couleur de chaque jour vient du calendrier des types de jour, affiché sous les versions une fois enregistré ; un jour sans couleur est facturé au prix bleu HP.',
      rules: 'Règles',
      rulesHint: 'La première règle qui correspond à une heure en fixe le prix ; sinon le prix par défaut. Mois, jours ou heures vides : tous.',
      ruleRate: 'Prix',
      ruleFrom: 'De',
      ruleTo: 'À',
      ruleDayTypes: 'Types de jour',
      ruleMonths: 'Mois',
      ruleWeekdays: 'Jours',
      addRule: 'Ajouter une règle',
      removeRule: 'Retirer',
      defaultRate: 'Prix par défaut',
      defaultRateHint: 'Quand aucune règle ne correspond.',
      fixed: 'Coût fixe par mois ({{currency}})',
      fixedHint: 'Abonnement et autres frais fixes, pour tout le site.',
      fixedLine: 'Coût fixe {{amount}} par mois, réparti : {{allocation}}.',
      allocationLabel: 'Réparti entre les équipements',
      allocation: {
        energy_share: 'Selon la consommation',
        equal: 'À parts égales',
      },
      allocationHint: {
        energy_share: 'Chaque équipement en porte une part proportionnelle à ce qu’il consomme.',
        equal: 'Chaque équipement mesuré en porte la même part.',
      },
      currency: 'Devise',
      recomputeHint: 'Les coûts sont recalculés dans la nuit à partir de la date de la version.',
      inForceError: 'Une version en vigueur ou passée reste telle qu’elle était : ajoutez une nouvelle version à la date du changement.',
      existsError: 'Le site a déjà une version à cette date.',
      errorRate: 'Saisissez un prix pour chaque tarif.',
      errorFixed: 'Le coût fixe n’est pas un montant valide.',
      errorHours: 'Saisissez les deux heures, ou aucune.',
    },
    days: {
      title: 'Types de jour (couleurs Tempo)',
      hint: 'Cliquez sur un jour pour changer son type ({{types}}), ou collez une liste de votre fournisseur.',
      hintViewer: 'Le type de chaque jour, d’après le fournisseur.',
      prev: 'Mois précédent',
      next: 'Mois suivant',
      save_one: 'Enregistrer {{count}} jour',
      save_other: 'Enregistrer {{count}} jours',
      saved_one: '{{count}} jour enregistré.',
      saved_other: '{{count}} jours enregistrés.',
      paste: 'Coller une liste',
      pasteLabel: 'Un jour par ligne',
      pasteHint: '« 2026-01-15 rouge » ou « 15/01/2026 blanc ».',
      import: 'Importer',
      nothing: 'Aucun jour n’a pu être lu.',
      invalid: 'Lignes non lues : {{lines}}',
    },
    month: {
      '1': 'janv.',
      '2': 'févr.',
      '3': 'mars',
      '4': 'avr.',
      '5': 'mai',
      '6': 'juin',
      '7': 'juil.',
      '8': 'août',
      '9': 'sept.',
      '10': 'oct.',
      '11': 'nov.',
      '12': 'déc.',
    },
  },
  haccpSync: {
    badge: {
      synced: 'Synchronisé',
      pending_one: '{{count}} en attente',
      pending_other: '{{count}} en attente',
      sending: 'Envoi…',
      offline: 'Hors ligne',
      offlinePending_one: 'Hors ligne · {{count}} en attente',
      offlinePending_other: 'Hors ligne · {{count}} en attente',
      signIn_one: '{{count}} en attente · reconnectez-vous',
      signIn_other: '{{count}} en attente · reconnectez-vous',
      failed_one: '{{count}} à revoir',
      failed_other: '{{count}} à revoir',
    },
    tag: {
      pending: 'En attente',
      failed: 'À revoir',
    },
    title: 'Enregistrements sur ce téléphone',
    offlineHint: 'Pas de réseau : les enregistrements sont gardés sur ce téléphone et envoyés dès le retour du réseau.',
    pendingHint: 'Ces enregistrements attendent d’être envoyés ; ils gardent l’heure de leur saisie.',
    unreachableHint: 'Le serveur est injoignable pour l’instant : un nouvel essai est fait automatiquement.',
    signInHint: 'Votre session a pris fin : reconnectez-vous pour envoyer ces enregistrements.',
    allSent: 'Tout ce qui a été saisi sur ce téléphone a été envoyé.',
    policy: 'Les enregistrements en attente ici sont les vôtres : si quelqu’un d’autre se connecte sur ce téléphone, il ne les voit pas et ne les envoie pas. Une déconnexion les garde, pour les envoyer à votre prochaine connexion (dans les 7 jours suivant la saisie).',
    sendNow: 'Envoyer maintenant',
    see: 'Voir',
    retry: 'Réessayer',
    discard: 'Retirer du téléphone',
    discardConfirm: 'Retirer cet enregistrement du téléphone ? Il n’a pas été enregistré : saisissez-le à nouveau si besoin.',
    failedTitle_one: '{{count}} enregistrement refusé',
    failedTitle_other: '{{count}} enregistrements refusés',
    failedHint: 'Saisis hors ligne, ils n’ont pas été acceptés par le serveur. Leur contenu est gardé : vérifiez-le, saisissez-les à nouveau si besoin, puis retirez-les.',
    queued: 'Gardé sur ce téléphone : il sera envoyé dès le retour du réseau.',
    full: 'Ce téléphone n’a plus de place pour garder des enregistrements en attente : envoyez d’abord ceux en attente, ou retirez la photo.',
    needsNetwork: 'Cette action demande le réseau.',
    waitingTick: 'Déjà cochée sur ce téléphone : elle sera envoyée dès le retour du réseau.',
    tickedAt: 'Cochée à {{time}} · en attente d’envoi',
    enteredAt: 'Saisi le {{time}}',
    withPhoto: 'avec une photo',
    task: 'Tâche',
    place: 'Où',
    signOutConfirm_one: '{{count}} enregistrement HACCP n’a pas encore été envoyé. Il reste sur ce téléphone et partira à votre prochaine connexion. Se déconnecter ?',
    signOutConfirm_other: '{{count}} enregistrements HACCP n’ont pas encore été envoyés. Ils restent sur ce téléphone et partiront à votre prochaine connexion. Se déconnecter ?',
    kind: {
      cooling: 'Refroidissement',
      reception: 'Réception',
      cleaning: 'Nettoyage',
    },
    error: {
      record_conflict: 'Un autre enregistrement a déjà été fait avec le même identifiant.',
      occurrence_done: 'Cette tâche avait déjà été cochée par quelqu’un d’autre.',
      task_archived: 'Cette tâche a été archivée entre-temps.',
      photo_lost: 'La photo n’a pas pu être gardée sur ce téléphone : saisissez à nouveau l’enregistrement.',
      validation_failed: 'Le serveur a refusé l’enregistrement (trop ancien, ou une valeur n’est plus valable).',
      feature_not_in_plan: 'Ce registre ne fait plus partie de votre formule.',
      forbidden: 'Vous n’avez plus le droit de saisir cet enregistrement.',
      other: 'Le serveur a refusé l’enregistrement.',
    },
  },
  cleaning: {
    today: 'Aujourd’hui',
    history: 'Historique',
    site: 'Site',
    summary: '{{done}} faites · {{todo}} à faire · {{overdue}} en retard',
    none: 'Rien à nettoyer aujourd’hui sur ce site.',
    noneHint: 'Les administrateurs établissent le plan de nettoyage dans Paramètres › HACCP.',
    noTasks: 'Pas encore de tâche de nettoyage',
    noTasksHint: 'Ajoutez les tâches du plan de nettoyage dans Paramètres › HACCP : équipement ou zone, quoi faire, à quelle fréquence.',
    general: 'Général',
    dueAt: 'avant {{time}}',
    dueEndOfDay: 'avant la fin de la journée',
    afterService: 'après {{service}}',
    status: {
      done: 'Fait',
      due: 'À faire',
      overdue: 'En retard',
    },
    doneBy: '{{name}} à {{time}}',
    late: 'en retard',
    tick: 'Cocher',
    tickTitle: 'Nettoyage fait',
    photo: 'Photo (facultatif)',
    comment: 'Commentaire (facultatif)',
    submit: 'Marquer comme fait',
    saved: 'Coché.',
    retry: 'Non envoyé (pas de connexion ?). Votre saisie est conservée : réessayez.',
    detail: 'Nettoyage fait',
    method: 'Méthode ou produit',
    field: {
      comment: 'Commentaire',
    },
    readOnly: 'Cocher demande le rôle Opérateur ou plus.',
    period: 'Période',
    days_one: '{{count}} dernier jour',
    days_other: '{{count}} derniers jours',
    noHistory: 'Rien de coché sur cette période.',
    occurrence: 'Pour le {{date}}',
    version: 'version {{version}}',
    voided: 'Annulé',
    open: 'Détails',
    tile: 'Nettoyage : {{done}} faites / {{total}} dues aujourd’hui',
    tileOverdue_one: '{{count}} en retard',
    tileOverdue_other: '{{count}} en retard',
    settings: {
      hint: 'Par site : les services (leur fin déclenche les tâches « après service »), puis les tâches. Modifier une tâche ne change jamais ce qui a été coché.',
      services: 'Services',
      servicesHint: 'Les tâches « après service » sont dues à la fin de chaque service (heure locale, le jour même). Par défaut : midi jusqu’à 15:00, soir jusqu’à 23:30.',
      defaults: 'Services par défaut : vérifiez-les, puis enregistrez.',
      serviceLabel: 'Service',
      serviceEnd: 'Fin à',
      addService: 'Ajouter un service',
      remove: 'Retirer',
      tasks: 'Tâches',
      addTask: 'Ajouter une tâche',
      noTasks: 'Aucune tâche sur ce site pour l’instant.',
      editTask: 'Modifier la tâche',
      newTask: 'Nouvelle tâche de nettoyage',
      archive: 'Archiver',
      archiveConfirm: 'Archiver cette tâche ? Elle n’apparaît plus dans la liste du jour ; ce qui a été coché reste dans le registre.',
      equipment: 'Équipement',
      noEquipment: '— aucun —',
      zone: 'Zone',
      zoneHint: 'Par ex. Cuisine, Plonge. Un équipement, une zone, ou les deux.',
      task: 'Tâche',
      method: 'Méthode ou produit',
      frequency: 'Fréquence',
      freq: {
        after_service: 'Après chaque service',
        daily: 'Tous les jours',
        weekly: 'Chaque semaine',
        monthly: 'Chaque mois',
      },
      weekdays: 'Jours',
      weekdaysHint: 'Aucun coché : tous les jours.',
      monthDay: 'Jour du mois',
      monthDayHint: '31 : le dernier jour des mois plus courts.',
      dueTime: 'À faire avant',
      dueTimeHint: 'Vide : avant la fin de la journée.',
      afterServices: 'Après les services',
      afterServicesHint: 'Aucun coché : après chaque service.',
      role: 'Confiée à',
      roleHint: 'Les rappels vont à ce rôle et aux rôles au-dessus.',
      remindAfter: 'Rappel après (min de retard)',
      escalate: 'Puis prévenir les administrateurs',
      escalateAfter: 'après (min de plus)',
      summaryDaily: 'tous les jours',
      summaryWeekly: 'chaque {{days}}',
      summaryMonthly: 'le {{day}} de chaque mois',
      summaryAfter: 'après {{services}}',
      summaryAt: 'avant {{time}}',
      version: 'v{{version}}',
    },
  },
  haccp: {
    title: 'Registres HACCP',
    subtitle: 'Refroidissements, réceptions et nettoyages, enregistrés au fil de l’eau.',
    notInPlan: 'Les registres HACCP ne font pas partie de votre formule.',
    notInPlanHint: 'Demandez-nous de les ajouter à votre abonnement.',
    tabs: {
      cooling: 'Refroidissement',
      reception: 'Réceptions',
      cleaning: 'Nettoyage',
    },
    export: {
      button: 'Exporter',
      title: 'Exporter les registres HACCP',
      hint: 'Un rapport PDF pour un contrôle, ou un registre en CSV. Les fichiers sont conservés 7 jours, aussi dans Paramètres › Exports.',
      site: 'Site',
      allSites: 'Tous les sites',
      from: 'Du',
      to: 'Au',
      days: 'Jours inclus, à l’heure locale de chaque site. PDF : {{pdf}} jours au plus ; CSV : {{csv}} jours.',
      toBeforeFrom: 'La fin précède le début.',
      tooLong: 'Trop long : {{days}} jours au plus.',
      pdf: 'Rapport PDF',
      pdfHint: 'Températures, alarmes et actions correctives, puis les registres de votre formule ; chaque page porte l’empreinte SHA-256 du rapport.',
      pdfNotInPlan: 'Le rapport PDF demande une formule supérieure.',
      csv: 'CSV, registre par registre',
      csvModule: {
        cooling: 'Refroidissements (CSV)',
        reception: 'Réceptions (CSV)',
        cleaning: 'Nettoyage (CSV)',
      },
      queued: 'Le fichier est en préparation : il apparaîtra ci-dessous.',
      recent: 'Exports HACCP récents',
      none: 'Aucun export HACCP pour l’instant.',
      close: 'Fermer',
    },
    soon: 'Bientôt',
    soonHint: 'Ce registre sera bientôt disponible ici.',
    history: {
      title: 'Corrections',
      none: 'Aucune correction : l’enregistrement est tel qu’il a été saisi.',
      correction: 'Correction',
      void: 'Annulé',
      by: '{{name}}, {{time}}',
      reason: 'Motif : {{reason}}',
      empty: '(vide)',
    },
    correct: {
      button: 'Corriger',
      title: 'Corriger cet enregistrement',
      hint: 'L’enregistrement lui-même ne change jamais : votre correction est conservée à côté, avec votre nom et le motif.',
      kind: 'Que voulez-vous faire ?',
      kindCorrection: 'Corriger des champs',
      kindVoid: 'Annuler l’enregistrement (saisi par erreur)',
      voidHint: 'Un enregistrement annulé reste dans le registre, barré.',
      reason: 'Motif',
      reasonHint: 'Obligatoire, 3 caractères au moins.',
      save: 'Enregistrer la correction',
      saveVoid: 'Annuler l’enregistrement',
      nothing: 'Modifiez au moins un champ.',
      voided: 'Annulé',
    },
    cooling: {
      start: 'Démarrer un refroidissement',
      running: 'En cours',
      recent: 'Cycles',
      none: 'Aucun refroidissement enregistré.',
      noneHint: 'Démarrez-en un quand une préparation chaude entre en cellule.',
      noRunning: 'Aucun refroidissement en cours.',
      noChiller: 'Pas encore de cellule de refroidissement',
      noChillerHint: 'Ajoutez un équipement de type « Cellule de refroidissement » avec une sonde, dans Paramètres › Sites et équipements.',
      chiller: 'Cellule de refroidissement',
      probe: 'Sonde',
      noProbe: 'Cette cellule n’a pas de sonde.',
      product: 'Produit',
      quantity: 'Quantité',
      quantityHint: 'Par ex. 12 kg, 3 bacs gastro.',
      photo: 'Photo (facultatif)',
      startedAt: 'Mise en cellule à',
      startedAtHint: 'Laissez tel quel si c’est maintenant.',
      submit: 'Démarrer le refroidissement',
      started: 'Refroidissement démarré.',
      retry: 'Non envoyé (pas de connexion ?). Votre saisie est conservée : réessayez.',
      remaining: 'encore {{time}}',
      overdue: '{{time}} après l’échéance',
      target: 'cible {{value}}',
      now: 'Actuellement {{value}}',
      deadline: 'Échéance {{time}}',
      reachedAt: 'Cible atteinte à {{time}} ({{value}})',
      lateAlarm: 'Cible non atteinte à temps : une alarme a été déclenchée.',
      openAlarm: 'Ouvrir l’alarme',
      auto: 'Démarré automatiquement',
      autoHint: 'Détecté sur une mise en cellule à chaud : renseignez le produit par une correction.',
      unknownProduct: 'Produit à renseigner',
      role: {
        air: 'sonde d’air',
        core: 'sonde à cœur',
      },
      status: {
        running: 'En cours',
        compliant: 'Conforme',
        non_compliant: 'Non conforme',
        cancelled: 'Annulé',
      },
      close: 'Clôturer',
      closeConfirm: 'Clôturer ce cycle ? La préparation est sortie de la cellule.',
      cancel: 'Annuler le cycle',
      cancelReason: 'Pourquoi annuler ce cycle ?',
      detail: 'Cycle de refroidissement',
      back: 'Tous les cycles',
      rule: 'De {{start}} à {{target}} en {{minutes}} min maximum',
      ruleHint: 'Règle en vigueur au démarrage du cycle.',
      events: 'Événements',
      event: {
        started: 'Démarré',
        target_reached: 'Cible atteinte',
        late: 'Échéance dépassée sans atteindre la cible',
        closed: 'Clôturé',
        cancelled: 'Annulé',
      },
      curve: 'Température',
      noCurve: 'Pas encore de relevé.',
      targetLine: 'Cible',
      filledAt: 'Saisi à {{time}}',
      syncedAt: 'reçu à {{time}}',
      field: {
        product: 'Produit',
        quantity: 'Quantité',
        probe_role: 'Rôle de la sonde',
        note: 'Remarque',
      },
      allSites: 'Tous les sites',
      allStatuses: 'Tous les statuts',
      site: 'Site',
      statusFilter: 'Statut',
    },
    reception: {
      new: 'Nouvelle réception',
      today: 'Aujourd’hui',
      days7: '7 derniers jours',
      days30: '30 derniers jours',
      period: 'Période',
      onlyNc: 'Non-conformités seulement',
      none: 'Aucune réception enregistrée sur cette période.',
      noneHint: 'Enregistrez chaque livraison à son arrivée : fournisseur, température, emballage, décision.',
      noSite: 'Ajoutez d’abord un site, dans Paramètres › Sites et équipements.',
      site: 'Site',
      allSites: 'Tous les sites',
      supplier: 'Fournisseur',
      supplierSearch: 'Tapez pour chercher…',
      supplierAdd: 'Ajouter le fournisseur « {{name}} »',
      supplierAdded: 'Fournisseur ajouté.',
      supplierPick: 'Choisissez un fournisseur dans la liste, ou ajoutez-le.',
      category: 'Catégorie de produits',
      noLimit: 'sans limite',
      product: 'Produits (facultatif)',
      productHint: 'Par ex. saumon, 2 colis ; numéro de lot.',
      temperature: 'Température',
      typeIn: 'Saisir',
      readProbe: 'Lire une sonde',
      temperatureC: 'Température (°C)',
      flipSign: 'Changer le signe',
      temperatureInvalid: 'Saisissez une température, par ex. 3,5 ou −18.',
      temperatureRequired: 'Cette catégorie demande une température.',
      probe: 'Sonde',
      noProbe: 'Ce site n’a pas de sonde de température avec un relevé récent.',
      probeReading: '{{value}} à {{time}}',
      probeStale: 'Pas de relevé récent',
      outOfLimit: 'Hors limite : {{limit}}',
      outOfLimitHint: 'Choisissez une décision et expliquez-la dans le commentaire.',
      limitMax: 'max {{value}}',
      limitMin: 'min {{value}}',
      packaging: 'Emballage intact et propre ?',
      yes: 'Oui',
      no: 'Non',
      deliveryNote: 'Photo du bon de livraison',
      deliveryNoteHint: 'Facultatif. Ouvre l’appareil photo sur un téléphone.',
      decision: 'Décision',
      comment: 'Commentaire',
      commentRequired: 'Obligatoire : expliquez la non-conformité, la réserve ou le refus.',
      commentOptional: 'Facultatif.',
      submit: 'Enregistrer la réception',
      saved: 'Réception enregistrée.',
      retry: 'Non envoyé (pas de connexion ?). Votre saisie est conservée : réessayez.',
      back: 'Toutes les réceptions',
      detail: 'Réception',
      receivedAt: 'Reçu à {{time}}',
      syncedAt: 'synchronisé à {{time}}',
      by: 'par {{name}}',
      fromProbe: 'lu sur {{probe}} à {{time}}',
      typedIn: 'saisie',
      limitsAtTime: 'Limites à la réception : {{limits}}',
      noTemperature: 'Pas de température',
      ncNotice: 'Non-conformité enregistrée.',
      decisions: {
        accepted: 'Acceptée',
        accepted_with_reserve: 'Acceptée avec réserve',
        refused: 'Refusée',
      },
      status: {
        void: 'Annulée',
        refused: 'Refusée',
        out_of_limit: 'Hors limite',
        packaging: 'Emballage',
        reserve: 'Avec réserve',
        accepted: 'Acceptée',
      },
      field: {
        supplier_id: 'Fournisseur',
        product: 'Produits',
        packaging_ok: 'Emballage intact',
        decision: 'Décision',
        comment: 'Commentaire',
      },
      settings: {
        hint: 'Les catégories de produits et leurs limites de température se règlent par site. Une réception garde les limites en vigueur au moment où elle a été enregistrée.',
        defaultsBadge: 'Valeurs par défaut à confirmer avec votre PMS',
        defaultsHint: 'Vérifiez ces limites avec votre PMS (plan de maîtrise sanitaire), ajustez-les si besoin, puis confirmez-les.',
        confirm: 'Confirmer',
        confirmed: 'Confirmé le {{date}}',
        edit: 'Modifier',
        archive: 'Archiver',
        restore: 'Rétablir',
        archived: 'Archivé',
        categories: 'Catégories de produits',
        addCategory: 'Ajouter une catégorie',
        name: 'Nom',
        minC: 'Minimum (°C)',
        maxC: 'Maximum (°C)',
        limitsHint: 'Laissez vide pour aucune limite. Une température égale à la limite est conforme.',
        suppliers: 'Fournisseurs',
        suppliersHint: 'Les opérateurs peuvent aussi ajouter un fournisseur depuis le formulaire de réception.',
        addSupplier: 'Ajouter un fournisseur',
        contact: 'Contact',
        notes: 'Notes',
        noSuppliers: 'Aucun fournisseur pour l’instant.',
        showArchived: 'Afficher les archivés',
      },
    },
    settings: {
      title: 'HACCP',
      retention: 'Conserver les registres HACCP pendant (années)',
      retentionHint: 'Les enregistrements plus anciens sont supprimés chaque nuit, avec leurs photos. 1 an minimum ; 5 ans par défaut.',
      cooling: 'Refroidissement',
      coolingHint: 'Par site. Chaque cellule du site suit ces valeurs ; un cycle garde celles en vigueur à son démarrage.',
      defaults: 'Valeurs par défaut : vérifiez-les avec votre PMS (plan de maîtrise sanitaire), puis enregistrez-les.',
      savedOn: 'Enregistré le {{date}}',
      startC: 'Température de départ (°C)',
      targetC: 'Température cible (°C)',
      maxMinutes: 'Durée maximale (min)',
      autoDetect: 'Démarrer un cycle automatiquement lors d’une mise en cellule à chaud',
      autoHint: 'Quand une sonde de la cellule monte d’autant en si peu de temps, un cycle démarre seul ; le produit est renseigné ensuite.',
      jumpC: 'Hausse de (°C)',
      jumpWindow: 'en (min)',
      reception: 'Réceptions',
      receptionHint: 'Catégories de produits et fournisseurs : bientôt.',
      cleaning: 'Nettoyage',
      cleaningHint: 'Le plan de nettoyage : services et tâches de chaque site.',
      noSites: 'Aucun site pour l’instant.',
    },
  },
};
